/* PeopleView + CompaniesView + DealsView + TasksView + CalendarView
   All wired to centralized useStore() — real CRUD, filters, bulk, sort, saved views. */
import { SearchField, EmptyState, Modal } from '../../shared/ui/index.jsx';
import { CfTableCell } from './crm-universal.jsx';

const { useState: useStateA, useMemo: useMemoA, useEffect: useEffectA } = React;
const emitView = (module, view) => window.dispatchEvent(new CustomEvent('nx-view-change', { detail: { module, view } }));

/* Company Score filter bands — matches Product definition 2026-08-14 */
const SCORE_BAND_FILTER_OPTIONS = [
  { label: 'Low Quality / Poor Fit',               min: 0,  max: 20  },
  { label: 'Average',                              min: 21, max: 50  },
  { label: 'Good Prospect',                        min: 51, max: 80  },
  { label: 'Highly Qualified / Strategic Account', min: 81, max: 100 },
];
const scoreBandMatches = (score, bandLabel) => {
  if (!bandLabel) return true;
  const band = SCORE_BAND_FILTER_OPTIONS.find(b => b.label === bandLabel);
  if (!band) return true;
  const v = score ?? 50;
  return v >= band.min && v <= band.max;
};

/* =========================================================
   CF-03: Custom Field Filter helpers (client-side, mirrors backend CfFilterService)
   ========================================================= */
const _CF_SUBTYPE_MAP = {
  TEXT: 'text', NUMBER: 'number', SELECT: 'select', MULTI_SELECT: 'multiselect',
  DATE: 'date', BOOLEAN: 'checkbox',
};
const _cfSubtypeForFilter = (def) => {
  try { const m = JSON.parse(def.formula || '{}'); if (m.subtype) return m.subtype; } catch {}
  return _CF_SUBTYPE_MAP[def.type] || 'text';
};
const _cfIsFilterable = (def) => {
  try { return JSON.parse(def.formula || '{}').filterable !== false; } catch { return true; }
};
const CF_OP_LABELS = {
  eq: 'is', neq: 'is not', contains: 'contains', starts_with: 'starts with',
  ends_with: 'ends with', gt: '>', gte: '≥', lt: '<', lte: '≤',
  before: 'before', after: 'after', is_empty: 'is empty', is_not_empty: 'is not empty',
  contains_any: 'contains any', contains_all: 'contains all',
};
const CF_SUBTYPE_OPS = {
  text:        ['eq','neq','contains','starts_with','ends_with','is_empty','is_not_empty'],
  textarea:    ['eq','neq','contains','starts_with','ends_with','is_empty','is_not_empty'],
  email:       ['eq','neq','contains','starts_with','ends_with','is_empty','is_not_empty'],
  phone:       ['eq','neq','contains','starts_with','ends_with','is_empty','is_not_empty'],
  url:         ['eq','neq','contains','starts_with','ends_with','is_empty','is_not_empty'],
  number:      ['eq','neq','gt','gte','lt','lte','is_empty','is_not_empty'],
  currency:    ['eq','neq','gt','gte','lt','lte','is_empty','is_not_empty'],
  percentage:  ['eq','neq','gt','gte','lt','lte','is_empty','is_not_empty'],
  date:        ['eq','before','after','is_empty','is_not_empty'],
  datetime:    ['eq','before','after','is_empty','is_not_empty'],
  checkbox:    ['eq','is_empty','is_not_empty'],
  select:      ['eq','neq','is_empty','is_not_empty'],
  multiselect: ['contains_any','contains_all','is_empty','is_not_empty'],
};
const _cfOpsForSubtype = (subtype) => CF_SUBTYPE_OPS[subtype] || CF_SUBTYPE_OPS.text;

const _cfMatchesFilter = (raw, op, value) => {
  const stored = raw ?? '';
  switch (op) {
    case 'is_empty':     return stored === '' || raw == null;
    case 'is_not_empty': return stored !== '' && raw != null;
    case 'eq':           return stored.toLowerCase() === String(value).toLowerCase();
    case 'neq':          return stored.toLowerCase() !== String(value).toLowerCase();
    case 'contains':     return stored.toLowerCase().includes(String(value).toLowerCase());
    case 'starts_with':  return stored.toLowerCase().startsWith(String(value).toLowerCase());
    case 'ends_with':    return stored.toLowerCase().endsWith(String(value).toLowerCase());
    case 'gt': case 'gte': case 'lt': case 'lte': {
      if (!/^-?\d+(\.\d+)?$/.test(stored)) return false;
      const n = parseFloat(stored), nv = parseFloat(String(value));
      if (isNaN(n) || isNaN(nv)) return false;
      if (op === 'gt')  return n > nv;
      if (op === 'gte') return n >= nv;
      if (op === 'lt')  return n < nv;
      return n <= nv;
    }
    case 'before': case 'after': {
      if (!/^\d{4}-\d{2}-\d{2}/.test(stored)) return false;
      const d = new Date(stored), dv = new Date(String(value));
      if (isNaN(d.getTime()) || isNaN(dv.getTime())) return false;
      return op === 'before' ? d < dv : d > dv;
    }
    case 'contains_any': {
      const items = stored.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      const vals  = Array.isArray(value) ? value : [String(value)];
      return vals.some(v => items.includes(String(v).trim().toLowerCase()));
    }
    case 'contains_all': {
      const items = stored.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      const vals  = Array.isArray(value) ? value : [String(value)];
      return vals.every(v => items.includes(String(v).trim().toLowerCase()));
    }
    default: return true;
  }
};

const applyCfFiltersClient = (list, filters, cfValues) => {
  const cfKeys = Object.keys(filters).filter(k => k.startsWith('cf_'));
  if (!cfKeys.length) return list;
  return list.filter(entity => {
    const entityVals = cfValues[entity.id] || {};
    return cfKeys.every(k => {
      const { op, value } = filters[k] || {};
      if (!op) return true;
      return _cfMatchesFilter(entityVals[k.slice(3)] ?? null, op, value || '');
    });
  });
};

const CfFilterPanel = ({ defs, filters, setFilter, onClose }) => {
  const filterableDefs = defs.filter(_cfIsFilterable);
  return (
    <div style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 120, minWidth: 300,
      background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
      borderRadius: 8, boxShadow: 'var(--nx-shadow-lg,0 4px 16px rgba(0,0,0,.12))',
      padding: 12, marginTop: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--nx-text)' }}>Custom Field Filters</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', lineHeight: 1, fontSize: 16 }} aria-label="Close">×</button>
      </div>
      {filterableDefs.length === 0 && (
        <p style={{ color: 'var(--nx-text-muted)', fontSize: 12, margin: 0 }}>No filterable custom fields defined.</p>
      )}
      {filterableDefs.map(def => {
        const subtype = _cfSubtypeForFilter(def);
        const ops = _cfOpsForSubtype(subtype);
        const current = filters['cf_' + def.id] || {};
        const currentOp = current.op || ops[0];
        const currentValue = current.value || '';
        const needsValue = !['is_empty','is_not_empty'].includes(currentOp);
        const isActive = !!filters['cf_' + def.id];
        const sel = { fontSize: 12, border: '1px solid var(--nx-border)', borderRadius: 4, padding: '2px 4px', background: 'var(--nx-surface)', color: 'var(--nx-text)' };
        return (
          <div key={def.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', minWidth: 80, flexShrink: 0 }}>{def.label}</span>
            <select style={sel} value={currentOp} onChange={e => {
              const newOp = e.target.value;
              setFilter('cf_' + def.id, { op: newOp, value: ['is_empty','is_not_empty'].includes(newOp) ? '' : currentValue });
            }}>
              {ops.map(op => <option key={op} value={op}>{CF_OP_LABELS[op] || op}</option>)}
            </select>
            {needsValue && (
              subtype === 'checkbox' ? (
                <select style={sel} value={currentValue} onChange={e => setFilter('cf_' + def.id, { op: currentOp, value: e.target.value })}>
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : subtype === 'select' && (def.options || []).length > 0 ? (
                <select style={sel} value={currentValue} onChange={e => setFilter('cf_' + def.id, { op: currentOp, value: e.target.value })}>
                  <option value="">—</option>
                  {(def.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input
                  type={['number','currency','percentage'].includes(subtype) ? 'number' : subtype === 'date' ? 'date' : 'text'}
                  value={currentValue}
                  placeholder="Value…"
                  style={{ ...sel, width: 110 }}
                  onChange={e => setFilter('cf_' + def.id, { op: currentOp, value: e.target.value })}
                />
              )
            )}
            {isActive && (
              <button onClick={() => setFilter('cf_' + def.id, null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: '0 2px', lineHeight: 1, fontSize: 14 }} aria-label={'Clear ' + def.label}>×</button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CfActiveFilterChips = ({ defs, filters, setFilter }) => {
  const cfKeys = Object.keys(filters).filter(k => k.startsWith('cf_'));
  if (!cfKeys.length) return null;
  return cfKeys.map(k => {
    const def = defs.find(d => d.id === k.slice(3));
    if (!def) return null;
    const { op, value } = filters[k] || {};
    const valLabel = Array.isArray(value) ? value.join(', ') : (value || '');
    const chipLabel = def.label + ': ' + (CF_OP_LABELS[op] || op || '') + (valLabel ? ' ' + valLabel : '');
    return (
      <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--nx-accent-alpha,rgba(99,102,241,.08))', color: 'var(--nx-accent)', border: '1px solid var(--nx-accent)', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 500 }}>
        {chipLabel}
        <button onClick={() => setFilter(k, null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-accent)', padding: 0, lineHeight: 1, fontSize: 13, marginLeft: 2 }} aria-label={'Remove ' + def.label + ' filter'}>×</button>
      </span>
    );
  });
};

/* LV-03: Writes filters + search + sort to sessionStorage for page-reload restoration.
   Columns are excluded — they are managed by store.setColumnsFromView (in-memory only).
   useFilters.setFilters / setSearch also write their own keys; this pre-writes them
   atomically so they're consistent if the page reloads before React state settles. */
const writeViewToSession = (module, view) => {
  try {
    sessionStorage.setItem('nx-filters-' + module, JSON.stringify(view.filters || {}));
    sessionStorage.setItem('nx-filters-' + module + '-search', view.search || '');
    if (view.sort?.field) {
      sessionStorage.setItem('nx-sort-' + module, JSON.stringify(view.sort));
    } else {
      sessionStorage.removeItem('nx-sort-' + module);
    }
  } catch (_) {}
};

/* =========================================================
   PEOPLE VIEW
   ========================================================= */
const PeopleView = () => {
  const store = useStore();
  const allPeople = store.people;
  const toast = useToast();
  const allOwners = useMemoA(() => [...new Set(allPeople.map(p => p.owner).filter(Boolean))], [allPeople]);

  const [view, setView] = useStateA('table');
  const [editing, setEditing] = useStateA(null);
  const [activeView, setActiveView] = useStateA('all');
  const [cfPickerOpen, setCfPickerOpen] = useStateA(false);
  const [cfFilterPanelOpen, setCfFilterPanelOpen] = useStateA(false);
  const [savedViewsOpen, setSavedViewsOpen] = useStateA(false);
  const [saveViewOpen, setSaveViewOpen] = useStateA(false);
  const [activeViewId, setActiveViewId] = useStateA(null); // LV-03: session-only active view indicator

  const peopleColumns = useColumns('people'); // LV-07: unified ordered columns (standard + CF)
  const peopleCfDefs = store.customFieldDefs?.people || [];
  const peopleCfValues = store.cfValues?.people || {};

  useEffectA(() => {
    if (activeView === 'bfsi' && !(store.modules || []).some(m => /bfsi/i.test(m))) {
      setActiveView('all');
    }
  }, [store.modules, activeView]);

  useEffectA(() => {
    if (peopleCfDefs.length > 0 && allPeople.length > 0) {
      store.loadCfValues('people', allPeople.map(p => p.id));
    }
  }, [allPeople, peopleCfDefs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fs = useFilters('people', {});
  const ts = useTableState('people', allPeople, { field: 'updatedAt', dir: 'desc' });

  const filtered = useMemoA(() => {
    let list = allPeople;
    if (activeView === 'new') list = list.filter(p => p.status === 'New');
    if (activeView === 'bfsi') list = list.filter(p => p.tags.some(t => t === 'BFSI' || t === 'NBFC'));
    if (fs.filters.owner) { const f = fs.filters.owner; list = list.filter(p => Array.isArray(f) ? f.includes(p.owner) : p.owner === f); }
    if (fs.filters.status) { const f = fs.filters.status; list = list.filter(p => Array.isArray(f) ? f.includes(p.status) : p.status === f); }
    if (fs.filters.tag) { const f = fs.filters.tag; list = list.filter(p => Array.isArray(f) ? f.some(t => p.tags.includes(t)) : p.tags.includes(f)); }
    if (fs.filters.city) { const f = fs.filters.city; list = list.filter(p => Array.isArray(f) ? f.includes(p.city) : p.city === f); }
    if (fs.filters.minScore) list = list.filter(p => p.score >= Number(fs.filters.minScore));
    if (fs.search) {
      const q = fs.search.toLowerCase();
      list = list.filter(p => (p.name + p.email + p.company + p.role).toLowerCase().includes(q));
    }
    list = applyCfFiltersClient(list, fs.filters, peopleCfValues);
    return ts.applySort(list);
  }, [allPeople, activeView, fs.filters, fs.search, ts, peopleCfValues]);

  const updateField = (id, field, value) => {
    store.update('people', id, { [field]: value });
  };

  const handleApplyView = (sv) => {
    writeViewToSession('people', sv);
    fs.setFilters(sv.filters || {});
    fs.setSearch(sv.search || '');
    ts.setSort(sv.sort ?? null);
    store.setColumnsFromView('people', sv.columns ?? null); // LV-03: in-memory only, no upsertDefault
    setActiveViewId(sv.id);
    ts.clear();
    setSavedViewsOpen(false);
    toast({ tone: 'success', icon: 'CheckCircle', title: 'View applied — ' + sv.name });
  };

  const handleRenameView = (view, newName) => {
    store.updateView(view.id, { name: newName });
    toast({ tone: 'success', icon: 'Edit', title: 'View renamed to “' + newName + '”' });
  };

  const handleOverwriteView = (view) => {
    store.updateView(view.id, {
      filters: { ...(fs.filters || {}), ...(fs.search ? { search: fs.search } : {}) },
      sort: ts.sort || undefined,
      columns: store.cfColumns?.people ?? undefined,
    });
    toast({ tone: 'success', icon: 'RefreshCw', title: 'View updated — ' + view.name });
  };

  const handleExport = async () => {
    const ids = ts.selected.size ? new Set(ts.selected) : null;
    const rows = ids ? filtered.filter(p => ids.has(p.id)) : filtered;
    if (!rows.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export', desc: 'Adjust filters and try again.' }); return; }
    const { cols, hadCfError } = await window._prepareCfExport('people', rows, store);
    window._download('people-export.csv', 'text/csv;charset=utf-8;', window._toCSV(rows, cols));
    if (hadCfError) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Exported ' + rows.length + ' rows', desc: 'Custom field values could not be loaded and were left blank.' });
    } else {
      toast({ tone: 'success', icon: 'File', title: 'Exported ' + rows.length + ' rows' });
    }
  };

  const bfsiEnabled = (store.modules || []).some(m => /bfsi/i.test(m));

  const tabs = [
    { id: 'all', label: 'All People', count: allPeople.length },
    ...(bfsiEnabled ? [{ id: 'bfsi', label: 'BFSI', count: allPeople.filter(p => p.tags.some(t => t === 'BFSI' || t === 'NBFC')).length }] : []),
  ];

  const allCities = [...new Set(allPeople.map(p => p.city))].filter(Boolean);
  const allTags = [...new Set(allPeople.flatMap(p => p.tags || []))].filter(Boolean);

  return (
    <div className="nx-view">
      <ModuleHeader
        title="People"
        count={filtered.length}
        tabs={tabs}
        activeTab={activeView}
        onTab={setActiveView}
        actions={
          <>
            <div className="nx-segment">
              <IconBtn icon="List" active={view === 'table'} onClick={() => { setView('table'); emitView('people','table'); }} title="Table" />
              <IconBtn icon="LayoutGrid" active={view === 'kanban'} onClick={() => { setView('kanban'); emitView('people','kanban'); }} title="Kanban" />
              <IconBtn icon="Calendar" active={view === 'calendar'} onClick={() => { setView('calendar'); emitView('people','calendar'); }} title="Calendar" />
            </div>
            <ExportMenu type="people" rows={ts.selected.size ? filtered.filter(p => ts.selected.has(p.id)) : filtered} />
            <ImportButton type="people" />
            <div style={{ position: 'relative' }}>
              <IconBtn icon="Columns" title="Customize columns" active={cfPickerOpen} onClick={() => setCfPickerOpen(o => !o)} />
              {cfPickerOpen && <ColumnManager entity="people" onClose={() => setCfPickerOpen(false)} />}
            </div>
            <div style={{ position: 'relative' }}>
              <Btn variant="ghost" icon="Star" onClick={() => setSavedViewsOpen(o => !o)}>Views</Btn>
              {savedViewsOpen && <SavedViewsPanel entity="people" onApply={handleApplyView} onRename={handleRenameView} onOverwrite={handleOverwriteView} onClose={() => setSavedViewsOpen(false)} />}
            </div>
            <Btn variant="secondary" icon="Bookmark" onClick={() => setSaveViewOpen(true)}>Save View</Btn>
            {saveViewOpen && (
              <SaveViewModal
                entity="people"
                onSave={(name) => {
                  store.saveView('people', name, fs.filters, fs.search, store.cfColumns?.people ?? null, ts.sort ?? null);
                  setSaveViewOpen(false);
                }}
                onClose={() => setSaveViewOpen(false)}
              />
            )}
            <Btn variant="primary" icon="Plus" onClick={() => store.openCreateForm('people')}>Add Person</Btn>
          </>
        }
        filters={
          <>
            <span className="nx-search-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flex: '0 0 auto' }}>
              <NexusIcon name="Search" size={13} style={{ position: 'absolute', left: 8, color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
              <input
                className="nx-search-input"
                style={{ paddingLeft: 28, width: 180 }}
                placeholder="Search people…"
                value={fs.search}
                onChange={e => fs.setSearch(e.target.value)}
                aria-label="Search people"
              />
              {fs.search && (
                <button
                  style={{ position: 'absolute', right: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 0, lineHeight: 1 }}
                  onClick={() => fs.setSearch('')}
                  aria-label="Clear search"
                ><NexusIcon name="X" size={11} /></button>
              )}
            </span>
            <FilterChip icon="User" label="Owner" value={fs.filters.owner} options={allOwners} onChange={v => fs.setFilter('owner', v)} active={!!fs.filters.owner} multi />
            <FilterChip icon="Tag" label="Status" value={fs.filters.status} options={['New', 'Engaged', 'Qualified', 'Customer']} onChange={v => fs.setFilter('status', v)} active={!!fs.filters.status} multi />
            <FilterChip icon="Tag" label="Tag" value={fs.filters.tag} options={allTags} onChange={v => fs.setFilter('tag', v)} active={!!fs.filters.tag} multi />
            <FilterChip icon="Globe" label="City" value={fs.filters.city} options={allCities} onChange={v => fs.setFilter('city', v)} active={!!fs.filters.city} multi />
            {peopleCfDefs.filter(_cfIsFilterable).length > 0 && (
              <div style={{ position: 'relative' }}>
                <IconBtn icon="SlidersHorizontal" title="Custom field filters" active={Object.keys(fs.filters).some(k => k.startsWith('cf_'))} onClick={() => setCfFilterPanelOpen(o => !o)} />
                {cfFilterPanelOpen && <CfFilterPanel defs={peopleCfDefs} filters={fs.filters} setFilter={fs.setFilter} onClose={() => setCfFilterPanelOpen(false)} />}
              </div>
            )}
            <CfActiveFilterChips defs={peopleCfDefs} filters={fs.filters} setFilter={fs.setFilter} />
            {fs.activeCount > 0 && (
              <Btn variant="ghost" size="sm" icon="X" onClick={fs.clearAll}>Clear ({fs.activeCount})</Btn>
            )}
          </>
        }
      />

      {ts.selected.size > 0 && (
        <BulkBar
          count={ts.selected.size}
          type="people"
          ids={[...ts.selected]}
          onClear={ts.clear}
          onExport={handleExport}
          extraActions={
            <>
              <Btn icon="Mail" variant="ghost" onClick={() => toast({ tone: 'success', icon: 'Mail', title: 'Email composer opened', desc: ts.selected.size + ' recipients' })}>Email</Btn>
              <Btn icon="UserPlus" variant="ghost" onClick={() => store.bulkUpdate('people', [...ts.selected], { owner: window.__nx_auth?.user?.name || '' })}>Reassign</Btn>
              <Btn icon="Tag" variant="ghost" onClick={() => store.bulkUpdate('people', [...ts.selected], { status: 'Engaged' })}>Mark Engaged</Btn>
            </>
          }
        />
      )}

      {view === 'table' && (
        <PeopleTable
          rows={filtered}
          ts={ts}
          total={allPeople.length}
          onOpen={store.openDrawer}
          onOpenCompany={(companyId) => { const co = (store.companies || []).find(c => c.id === companyId); if (co) store.openDrawer(co); }}
          editing={editing}
          setEditing={setEditing}
          updateField={updateField}
          columns={peopleColumns}
          defs={peopleCfDefs}
          cfValues={peopleCfValues}
        />
      )}
      {view === 'kanban' && <PeopleKanban rows={filtered} onOpen={store.openDrawer} onMove={(id, status) => store.update('people', id, { status })} />}
      {view === 'calendar' && <PeopleCalendar rows={filtered} onOpen={store.openDrawer} />}
    </div>
  );
};

/* LV-07: PeopleTable — driven by unified columns array (standard + CF, ordered) */
const PeopleTable = ({ rows, ts, total, onOpen, onOpenCompany, editing, setEditing, updateField, columns = [], defs = [], cfValues = {} }) => {
  const allSelected = rows.length > 0 && rows.every(r => ts.selected.has(r.id));
  const CF_BADGE = <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--nx-accent)', background: 'var(--nx-accent-tint, rgba(99,102,241,.1))', borderRadius: 3, padding: '1px 4px' }}>CF</span>;
  return (
    <div className="nx-table-wrap">
      <div className="nx-table-scroll">
        <table className="nx-table">
          <thead>
            <tr>
              <th className="nx-th-check"><input type="checkbox" className="nx-checkbox" checked={allSelected} onChange={() => ts.toggleAll(rows)} /></th>
              <SortableTh icon="User" sticky field="name" sort={ts.sort} onSort={ts.onSort}>Name</SortableTh>
              {columns.map(col => col.isCf
                ? <Th key={col.field} style={{ width: 150 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>{col.label}{CF_BADGE}</span></Th>
                : col.sortField
                  ? <SortableTh key={col.field} icon={col.icon} field={col.sortField} sort={ts.sort} onSort={ts.onSort} style={{ width: col.colWidth }}>{col.label}</SortableTh>
                  : <Th key={col.field} icon={col.icon} style={{ width: col.colWidth }}>{col.label}</Th>
              )}
              <th scope="col" className="nx-th-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <PeopleRow key={p.id} p={p}
                checked={ts.selected.has(p.id)}
                onCheck={() => ts.toggleOne(p.id)}
                onOpen={() => onOpen(p)}
                onOpenCompany={onOpenCompany}
                editing={editing}
                setEditing={setEditing}
                updateField={updateField}
                columns={columns}
                defs={defs}
                cfValues={cfValues}
              />
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={3 + columns.length}><EmptyState icon="Search" title="No people match your filters." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="nx-table-foot">
        <span>{rows.length} of {total ?? rows.length}</span>
        <span className="nx-foot-sep">·</span>
        <span>Updated just now</span>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" icon="ChevronLeft" />
        <span className="nx-page">Page 1 of 1</span>
        <Btn variant="ghost" iconRight="ChevronRight" />
      </div>
    </div>
  );
};

const Th = ({ children, icon, sticky, style }) => (
  <th className={sticky ? 'is-sticky' : ''} style={style}>
    <span className="nx-th-inner">
      {icon && <NexusIcon name={icon} size={12} />}
      <span>{children}</span>
    </span>
  </th>
);
window.Th = Th;

/* CF-02: Derives visible CF column configs from store */
const useCfColumns = (entity) => {
  const store = useStore();
  const defs = (store.customFieldDefs?.[entity] || []).filter(d => d.status !== 'ARCHIVED');
  const savedIds = store.cfColumns?.[entity]; // null = all visible; string[] = specific
  if (!savedIds) return defs.map(d => ({ field: 'cf_' + d.id, label: d.label, cfFieldId: d.id }));
  return defs
    .filter(d => savedIds.includes('cf_' + d.id))
    .map(d => ({ field: 'cf_' + d.id, label: d.label, cfFieldId: d.id }));
};

/* LV-07: Primary (locked) column per entity — always first, no drag/checkbox */
const STANDARD_COLUMN_PRIMARY = {
  people:    { field: 'name',  label: 'Name',    icon: 'User',     sortField: 'name' },
  companies: { field: 'name',  label: 'Company', icon: 'Building', sortField: 'name' },
  deals:     { field: 'title', label: 'Deal',    icon: 'Target',   sortField: 'title' },
};

/* LV-07: Hideable standard columns per entity — snake_case field IDs */
/* CRM-UX-017: colWidth (px) defines the target column width for table-layout:fixed.
   Distribution of any surplus viewport space is proportional to these widths — not to
   natural content widths — giving a predictable, balanced layout across all column configs. */
const STANDARD_COLUMN_REGISTRY = {
  people: [
    { field: 'email',         label: 'Email',         icon: 'Mail',        sortField: 'email',        colWidth: 180 },
    { field: 'phone',         label: 'Phone',         icon: 'Phone',       sortField: 'phone',        colWidth: 140 },
    { field: 'role',          label: 'Role',          icon: 'Briefcase',   sortField: 'role',         colWidth: 150 },
    { field: 'company',       label: 'Company',       icon: 'Building',    sortField: 'company',      colWidth: 160 },
    { field: 'status',        label: 'Status',        icon: 'Tag',         sortField: 'status',       colWidth: 110 },
    { field: 'score',         label: 'Score',         icon: 'TrendingUp',  sortField: 'score',        colWidth: 80  },
    { field: 'owner',         label: 'Owner',         icon: 'User',        sortField: 'owner',        colWidth: 130 },
    { field: 'city',          label: 'City',          icon: 'Globe',       sortField: 'city',         colWidth: 110 },
    { field: 'tags',          label: 'Tags',          icon: 'Tag',         sortField: null,           colWidth: 150 },
    { field: 'last_activity', label: 'Last Activity', icon: 'Clock',       sortField: 'lastActivity', colWidth: 130 },
  ],
  companies: [
    { field: 'domain',        label: 'Domain',        icon: 'Link',        sortField: 'domain',       colWidth: 160 },
    { field: 'industry',      label: 'Industry',      icon: 'Briefcase',   sortField: 'industry',     colWidth: 150 },
    { field: 'employees',     label: 'Employees',     icon: 'Users',       sortField: 'employees',    colWidth: 100 },
    { field: 'revenue',       label: 'Revenue',       icon: 'DollarSign',  sortField: 'revenue',      colWidth: 120 },
    { field: 'location',      label: 'Location',      icon: 'Globe',       sortField: 'location',     colWidth: 140 },
    { field: 'lifecycle',     label: 'Lifecycle',     icon: 'Tag',         sortField: 'lifecycle',    colWidth: 120 },
    { field: 'score',         label: 'Score',         icon: 'TrendingUp',  sortField: 'score',        colWidth: 80  },
    { field: 'open_deals',    label: 'Open Deals',    icon: 'Target',      sortField: 'deals',        colWidth: 100 },
    { field: 'owner',         label: 'Owner',         icon: 'User',        sortField: 'owner',        colWidth: 130 },
    { field: 'last_activity', label: 'Last Activity', icon: 'Clock',       sortField: 'lastActivity', colWidth: 130 },
  ],
  deals: [
    { field: 'company',    label: 'Company',    icon: 'Building',    sortField: 'companyId', colWidth: 160 },
    { field: 'value',      label: 'Value',      icon: 'DollarSign',  sortField: 'value',     colWidth: 110 },
    { field: 'stage',      label: 'Stage',      icon: 'Tag',         sortField: 'stageId',   colWidth: 130 },
    { field: 'priority',   label: 'Priority',   icon: 'AlertCircle', sortField: 'priority',  colWidth: 100 },
    { field: 'close_date', label: 'Close Date', icon: 'Calendar',    sortField: 'closeDate', colWidth: 120 },
    { field: 'age',        label: 'Age',        icon: 'Clock',       sortField: 'age',       colWidth: 80  },
    { field: 'owner',      label: 'Owner',      icon: 'User',        sortField: 'owner',     colWidth: 130 },
    { field: 'last_touch', label: 'Last Touch', icon: 'Clock',       sortField: 'lastTouch', colWidth: 130 },
  ],
};
window.STANDARD_COLUMN_REGISTRY = STANDARD_COLUMN_REGISTRY;
window.STANDARD_COLUMN_PRIMARY = STANDARD_COLUMN_PRIMARY;

/* LV-07: Detect legacy CF-only column arrays (pre-LV-07 format containing only cf_ IDs) */
const _isLegacyCfOnlyFormat = (ids) => {
  if (!ids || ids.length === 0) return false;
  return ids.every(id => id.startsWith('cf_'));
};
window._isLegacyCfOnlyFormat = _isLegacyCfOnlyFormat;

/* LV-07: Full ordered list (visible + hidden) for ColumnManager internal display */
const _buildAllColIds = (registry, cfDefs, savedIds) => {
  const standardIds = registry.map(r => r.field);
  const cfIds = cfDefs.map(d => 'cf_' + d.id);
  if (!savedIds) return [...standardIds, ...cfIds];
  if (_isLegacyCfOnlyFormat(savedIds)) {
    const visibleCf = savedIds.filter(id => cfIds.includes(id));
    const hiddenCf = cfIds.filter(id => !savedIds.includes(id));
    return [...standardIds, ...visibleCf, ...hiddenCf];
  }
  const validVisible = savedIds.filter(id => id.startsWith('cf_') ? cfIds.includes(id) : standardIds.includes(id));
  const hidden = [...standardIds, ...cfIds].filter(id => !validVisible.includes(id));
  return [...validVisible, ...hidden];
};
window._buildAllColIds = _buildAllColIds;

/* LV-07: Set of visible column IDs from savedIds */
const _buildVisibleSet = (registry, cfDefs, savedIds) => {
  const standardIds = registry.map(r => r.field);
  const cfIds = cfDefs.map(d => 'cf_' + d.id);
  if (!savedIds) return new Set([...standardIds, ...cfIds]);
  if (_isLegacyCfOnlyFormat(savedIds)) {
    return new Set([...standardIds, ...savedIds.filter(id => cfIds.includes(id))]);
  }
  return new Set(savedIds.filter(id => id.startsWith('cf_') ? cfIds.includes(id) : standardIds.includes(id)));
};
window._buildVisibleSet = _buildVisibleSet;

/* LV-07: Unified column hook — visible ordered column configs for People, Companies, Deals.
   Tasks keeps useCfColumns. */
const useColumns = (entity) => {
  const store = useStore();
  const cfDefs = (store.customFieldDefs?.[entity] || []).filter(d => d.status !== 'ARCHIVED');
  const registry = STANDARD_COLUMN_REGISTRY[entity] || [];
  const savedIds = store.cfColumns?.[entity];
  if (!savedIds) {
    return [
      ...registry.map(r => ({ ...r, isCf: false })),
      ...cfDefs.map(d => ({ field: 'cf_' + d.id, label: d.label, icon: null, sortField: null, isCf: true, cfFieldId: d.id })),
    ];
  }
  if (_isLegacyCfOnlyFormat(savedIds)) {
    return [
      ...registry.map(r => ({ ...r, isCf: false })),
      ...cfDefs.filter(d => savedIds.includes('cf_' + d.id)).map(d => ({ field: 'cf_' + d.id, label: d.label, icon: null, sortField: null, isCf: true, cfFieldId: d.id })),
    ];
  }
  const result = [];
  for (const id of savedIds) {
    if (id.startsWith('cf_')) {
      const def = cfDefs.find(d => 'cf_' + d.id === id);
      if (def) result.push({ field: id, label: def.label, icon: null, sortField: null, isCf: true, cfFieldId: def.id });
    } else {
      const reg = registry.find(r => r.field === id);
      if (reg) result.push({ ...reg, isCf: false });
    }
  }
  return result;
};

/* LV-07: Unified Column Manager panel — replaces CfColumnPicker for People, Companies, Deals.
   CfColumnPicker is kept for Tasks (CF chips only, per Design-002). */
const ColumnManager = ({ entity, onClose }) => {
  const store = useStore();
  const cfDefs = (store.customFieldDefs?.[entity] || []).filter(d => d.status !== 'ARCHIVED');
  const registry = STANDARD_COLUMN_REGISTRY[entity] || [];
  const savedIds = store.cfColumns?.[entity];
  const [localOrder, setLocalOrder] = useStateA(() => _buildAllColIds(registry, cfDefs, savedIds));
  const [dragIdx, setDragIdx] = useStateA(null);

  const visibleSet = _buildVisibleSet(registry, cfDefs, savedIds);
  const isAvailable = (colId) => colId.startsWith('cf_') ? cfDefs.some(d => 'cf_' + d.id === colId) : registry.some(r => r.field === colId);
  const getLabel = (colId) => colId.startsWith('cf_') ? (cfDefs.find(d => 'cf_' + d.id === colId)?.label || colId) : (registry.find(r => r.field === colId)?.label || colId);

  const persistOrder = (newOrder) => {
    const visibleIds = newOrder.filter(id => isAvailable(id) && visibleSet.has(id));
    store.setCfColumns(entity, visibleIds);
  };

  const toggleColumn = (colId) => {
    const currentVisible = localOrder.filter(id => isAvailable(id) && visibleSet.has(id));
    if (visibleSet.has(colId) && currentVisible.length <= 1) return;
    if (visibleSet.has(colId)) {
      store.setCfColumns(entity, currentVisible.filter(id => id !== colId));
    } else {
      const canonicalIds = [
        ...registry.map(r => r.field),
        ...cfDefs.map(d => 'cf_' + d.id),
      ];
      const rank = Object.fromEntries(canonicalIds.map((id, i) => [id, i]));
      const colRank = rank[colId] ?? Infinity;
      let insertAt = 0;
      currentVisible.forEach((id, i) => {
        if ((rank[id] ?? Infinity) < colRank) insertAt = i + 1;
      });
      store.setCfColumns(entity, [
        ...currentVisible.slice(0, insertAt),
        colId,
        ...currentVisible.slice(insertAt),
      ]);
    }
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const newOrder = [...localOrder];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, moved);
    setLocalOrder(newOrder);
    persistOrder(newOrder);
    setDragIdx(null);
  };

  const primary = STANDARD_COLUMN_PRIMARY[entity];
  const CF_BADGE = <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--nx-accent)', background: 'var(--nx-accent-tint, rgba(99,102,241,.1))', borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>CF</span>;

  return (
    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 200, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, minWidth: 260, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--nx-border)' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>Columns</span>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: 'var(--nx-text-muted)' }} onClick={onClose} aria-label="Close column manager">
          <NexusIcon name="X" size={13} />
        </button>
      </div>
      <div style={{ padding: '4px 0', maxHeight: 360, overflowY: 'auto' }}>
        {primary && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', fontSize: 13, color: 'var(--nx-text-muted)', userSelect: 'none' }}>
            <NexusIcon name="Lock" size={12} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{primary.label}</span>
          </div>
        )}
        {localOrder.filter(isAvailable).map((colId, idx) => {
          const isCf = colId.startsWith('cf_');
          const visible = visibleSet.has(colId);
          return (
            <div key={colId}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={e => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', fontSize: 13, color: 'var(--nx-text)', cursor: 'grab', borderRadius: 4 }}
            >
              <span style={{ color: 'var(--nx-text-muted)', fontSize: 11, lineHeight: 1, flexShrink: 0 }} aria-hidden>⠿</span>
              <input type="checkbox" className="nx-checkbox" checked={visible} onChange={() => toggleColumn(colId)} aria-label={(visible ? 'Hide' : 'Show') + ' ' + getLabel(colId)} />
              <span style={{ flex: 1 }}>{getLabel(colId)}</span>
              {isCf && CF_BADGE}
            </div>
          );
        })}
      </div>
      <div style={{ borderTop: '1px solid var(--nx-border)', padding: '6px 12px' }}>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--nx-accent)', padding: 0 }} onClick={() => store.setCfColumns(entity, null)}>Restore defaults</button>
      </div>
    </div>
  );
};

/* LV-04: RenameViewModal — pre-fills current view name, validates, calls onRename(newName).
   Blocked on empty, '__default__', or unchanged name. Duplicate name shows warning only (OD-05). */
const RenameViewModal = ({ view, entity, onRename, onClose }) => {
  const store = useStore();
  const [name, setName] = useStateA(view.name);

  const existingViews = (store.savedViews || []).filter(v => v.module === entity && v.id !== view.id);
  const trimmed = name.trim();
  const isReserved = trimmed === '__default__';
  const isDuplicate = trimmed !== '' && !isReserved && existingViews.some(v => v.name === trimmed);
  const isUnchanged = trimmed !== '' && !isReserved && trimmed === view.name.trim();
  const canSubmit = trimmed !== '' && !isReserved && !isUnchanged;

  const handleRename = () => { if (canSubmit) onRename(trimmed); };

  return (
    <Modal
      title="Rename View"
      icon="Edit"
      onClose={onClose}
      width={400}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={handleRename} disabled={!canSubmit}>Rename</Btn>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', display: 'block', marginBottom: 6 }}>
            New name
          </label>
          <input
            className="nx-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') onClose(); }}
            autoFocus
          />
        </div>
        {isReserved && (
          <p style={{ fontSize: 12, color: 'var(--nx-danger)', margin: 0 }}>
            The name &quot;__default__&quot; is reserved and cannot be used.
          </p>
        )}
        {isDuplicate && (
          <p style={{ fontSize: 12, color: 'var(--nx-warning, #b45309)', margin: 0 }}>
            Another view already has this name.
          </p>
        )}
        {isUnchanged && (
          <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0 }}>
            Enter a different name to rename this view.
          </p>
        )}
      </div>
    </Modal>
  );
};

/* LV-02: Saved Views panel — lists named saved views for an entity.
   Loading and error states use store.loading / store.loadError.
   __default__ is already excluded from store.savedViews at the loadWorkspace level.
   LV-04: Added onRename and onOverwrite for Rename + Overwrite actions per view row. */
const SavedViewsPanel = ({ entity, onApply, onRename, onOverwrite, onClose }) => {
  const store = useStore();
  const [deletingId, setDeletingId] = useStateA(null);
  const [overwritingId, setOverwritingId] = useStateA(null);
  const [renamingView, setRenamingView] = useStateA(null);
  const views = (store.savedViews || []).filter(v => v.module === entity);
  // LV-05: ownership — actions only available on views the current user owns
  const currentMemberId = store.currentMemberId ?? null;

  const panelStyle = {
    position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 200,
    background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
    borderRadius: 8, padding: 8, minWidth: 220, maxWidth: 320,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  };
  const header = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 8px', borderBottom: '1px solid var(--nx-border)', marginBottom: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>Saved Views</span>
      <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: 'var(--nx-text-muted)' }} onClick={onClose}>
        <NexusIcon name="X" size={13} />
      </button>
    </div>
  );

  if (store.loading) {
    return (
      <div style={panelStyle}>
        {header}
        <p style={{ color: 'var(--nx-text-muted)', fontSize: 13, margin: '8px', textAlign: 'center' }}>Loading…</p>
      </div>
    );
  }

  if (store.loadError && views.length === 0) {
    return (
      <div style={panelStyle}>
        {header}
        <p style={{ color: 'var(--nx-danger)', fontSize: 13, margin: '4px 8px' }}>Could not load views. Try refreshing.</p>
      </div>
    );
  }

  const iconBtn = (label, iconName, onClick) => (
    <button
      type="button"
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}
      onClick={onClick}
      aria-label={label}
    >
      <NexusIcon name={iconName} size={12} />
    </button>
  );

  return (
    <div style={panelStyle}>
      {header}
      {views.length === 0
        ? <p style={{ color: 'var(--nx-text-muted)', fontSize: 13, margin: '4px 8px' }}>No saved views. Use &quot;Save View&quot; to create one.</p>
        : views.map(v => {
          const canEdit = v.memberId === currentMemberId;
          return (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 4px', borderRadius: 4 }}>
              {overwritingId === v.id ? (
                <>
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--nx-text-muted)', padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Update &quot;{v.name}&quot;?
                  </span>
                  <Btn size="sm" variant="ghost" style={{ color: 'var(--nx-primary)' }} onClick={() => { onOverwrite(v); setOverwritingId(null); }}>Update</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setOverwritingId(null)}>Cancel</Btn>
                </>
              ) : deletingId === v.id ? (
                <>
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--nx-text-muted)', padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Delete &quot;{v.name}&quot;?
                  </span>
                  <Btn size="sm" variant="ghost" style={{ color: 'var(--nx-danger)' }} onClick={() => { store.removeView(v.id); setDeletingId(null); }}>Delete</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setDeletingId(null)}>Cancel</Btn>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--nx-text)', padding: '4px 6px', borderRadius: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    onClick={() => onApply(v)}
                    title={'Apply view: ' + v.name}
                  >
                    {v.name}
                  </button>
                  {canEdit && iconBtn('Rename view: ' + v.name, 'Pencil', () => setRenamingView(v))}
                  {canEdit && iconBtn('Overwrite view: ' + v.name, 'RefreshCw', () => setOverwritingId(v.id))}
                  {canEdit && iconBtn('Delete view: ' + v.name, 'Trash2', () => setDeletingId(v.id))}
                </>
              )}
            </div>
          );
        })
      }
      {renamingView && (
        <RenameViewModal
          view={renamingView}
          entity={entity}
          onRename={(newName) => { onRename(renamingView, newName); setRenamingView(null); }}
          onClose={() => setRenamingView(null)}
        />
      )}
    </div>
  );
};

/* PHASE-A: SaveViewModal — replaces window.prompt() and inline name inputs across all four entities.
   Accepts the view name from the user, validates it, and calls onSave(name) on confirmation.
   Duplicate name detection shows a warning but does not block save (design decision OD-05).
   The reserved name '__default__' is always blocked. */
const SaveViewModal = ({ entity, onSave, onClose }) => {
  const store = useStore();
  const [name, setName] = useStateA('');

  const existingViews = (store.savedViews || []).filter(v => v.module === entity);
  const trimmed = name.trim();
  const isReserved = trimmed === '__default__';
  const isDuplicate = trimmed !== '' && !isReserved && existingViews.some(v => v.name === trimmed);
  const canSubmit = trimmed !== '' && !isReserved;

  const handleSave = () => {
    if (!canSubmit) return;
    onSave(trimmed);
  };

  return (
    <Modal
      title="Save View"
      icon="Bookmark"
      onClose={onClose}
      width={400}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={!canSubmit}>Save View</Btn>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', display: 'block', marginBottom: 6 }}>
            View name
          </label>
          <input
            className="nx-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            placeholder="e.g. My Customers, High Value Leads…"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onClose(); }}
            autoFocus
          />
        </div>
        {isReserved && (
          <p style={{ fontSize: 12, color: 'var(--nx-danger)', margin: 0 }}>
            The name &quot;__default__&quot; is reserved and cannot be used.
          </p>
        )}
        {isDuplicate && (
          <p style={{ fontSize: 12, color: 'var(--nx-warning, #b45309)', margin: 0 }}>
            A view with this name already exists. Saving will create a second view with the same name.
          </p>
        )}
      </div>
    </Modal>
  );
};

/* CF-02: Column picker floating panel */
const CfColumnPicker = ({ entity, onClose }) => {
  const store = useStore();
  const defs = (store.customFieldDefs?.[entity] || []).filter(d => d.status !== 'ARCHIVED');
  const savedIds = store.cfColumns?.[entity];
  const isVisible = (def) => !savedIds || savedIds.includes('cf_' + def.id);

  const toggle = (def) => {
    const colId = 'cf_' + def.id;
    const current = savedIds ?? defs.map(d => 'cf_' + d.id);
    const next = current.includes(colId)
      ? current.filter(id => id !== colId)
      : [...current, colId];
    store.setCfColumns(entity, next);
  };

  return (
    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 200, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: 8, minWidth: 220, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 8px', borderBottom: '1px solid var(--nx-border)', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>Custom Columns</span>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: 'var(--nx-text-muted)' }} onClick={onClose}>
          <NexusIcon name="X" size={13} />
        </button>
      </div>
      {defs.length === 0
        ? <p style={{ color: 'var(--nx-text-muted)', fontSize: 13, margin: '4px 8px' }}>No custom fields defined.</p>
        : defs.map(d => (
          <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', borderRadius: 4, fontSize: 13, color: 'var(--nx-text)' }}>
            <input type="checkbox" className="nx-checkbox" checked={isVisible(d)} onChange={() => toggle(d)} />
            <span>{d.label}</span>
          </label>
        ))
      }
    </div>
  );
};

/* LV-07: PeopleRow — switch-dispatch renderer driven by unified columns array */
const PeopleRow = ({ p, checked, onCheck, onOpen, onOpenCompany, editing, setEditing, updateField, columns = [], defs = [], cfValues = {} }) => {
  const isEditing = (field) => editing && editing.id === p.id && editing.field === field;
  const rowCfValues = cfValues[p.id] || {};
  const renderCell = (col) => {
    if (col.isCf) return <td key={col.field}><CfTableCell fieldId={col.cfFieldId} defs={defs} raw={rowCfValues[col.cfFieldId] ?? null} /></td>;
    switch (col.field) {
      case 'email':         return <EditableCell key="email" value={p.email} editing={isEditing('email')} onStart={() => setEditing({ id: p.id, field: 'email' })} onSave={(v) => { updateField(p.id, 'email', v); setEditing(null); }} onCancel={() => setEditing(null)} />;
      case 'phone':         return <EditableCell key="phone" value={p.phone} editing={isEditing('phone')} onStart={() => setEditing({ id: p.id, field: 'phone' })} onSave={(v) => { updateField(p.id, 'phone', v); setEditing(null); }} onCancel={() => setEditing(null)} mono />;
      case 'role':          return <EditableCell key="role"  value={p.role}  editing={isEditing('role')}  onStart={() => setEditing({ id: p.id, field: 'role' })}  onSave={(v) => { updateField(p.id, 'role',  v); setEditing(null); }} onCancel={() => setEditing(null)} />;
      case 'company':       return <td key="company">{p.companyId && onOpenCompany ? <button type="button" className="nx-link-cell" style={{ background: 'none', border: 'none', padding: 0, font: 'inherit' }} onClick={e => { e.stopPropagation(); onOpenCompany(p.companyId); }}>{p.company}</button> : <span className="nx-muted">{p.company || '—'}</span>}</td>;
      case 'status':        { const t = { New: 'gray', Engaged: 'blue', Qualified: 'amber', Customer: 'green' }[p.status] || 'gray'; return <td key="status"><Pill tone={t}>{p.status}</Pill></td>; }
      case 'score':         return <td key="score"><Score value={p.score} /></td>;
      case 'owner':         return <td key="owner"><span className="nx-owner-cell"><Avatar name={p.owner} tone="var(--nx-accent)" size={18} /><span>{(p.owner || '').split(' ')[0] || '—'}</span></span></td>;
      case 'city':          return <td key="city"><span className="nx-muted">{p.city}</span></td>;
      case 'tags':          return <td key="tags"><span className="nx-tag-cell">{(p.tags || []).map(t => <span key={t} className="nx-tag">{t}</span>)}</span></td>;
      case 'last_activity': return <td key="last_activity"><span className="nx-muted">{p.lastActivity}</span></td>;
      default:              return <td key={col.field} />;
    }
  };
  return (
    <tr className={'nx-tr' + (checked ? ' is-checked' : '')}>
      <td className="nx-td-check"><input type="checkbox" className="nx-checkbox" checked={checked} onChange={onCheck} onClick={e => e.stopPropagation()} /></td>
      <td className="is-sticky" onClick={onOpen}>
        <button className="nx-row-name">
          <Avatar name={p.name} tone={p.tone} size={22} style={{ flexShrink: 0 }} />
          <span className="nx-row-name-text">{p.salutation ? `${p.salutation} ${p.name}` : p.name}</span>
          <NexusIcon name="ArrowUpRight" size={11} className="nx-row-arrow" style={{ flexShrink: 0 }} />
        </button>
      </td>
      {columns.map(col => renderCell(col))}
      <td className="nx-td-end"><RowActions type="people" record={p} /></td>
    </tr>
  );
};

const EditableCell = ({ value, editing, onStart, onSave, onCancel, mono }) => {
  const [val, setVal] = useStateA(value);
  React.useEffect(() => { setVal(value); }, [value, editing]);
  if (editing) {
    return (
      <td className="nx-td-edit">
        <input
          autoFocus
          className={'nx-cell-input' + (mono ? ' is-mono' : '')}
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => onSave(val.trim())}
          onKeyDown={e => {
            if (e.key === 'Enter') onSave(val.trim());
            if (e.key === 'Escape') onCancel();
          }}
        />
      </td>
    );
  }
  return <td onDoubleClick={onStart}><span className={'nx-cell ' + (mono ? 'is-mono' : '')}>{value}</span></td>;
};
window.EditableCell = EditableCell;

const PeopleKanban = ({ rows, onOpen, onMove }) => {
  const cols = ['New', 'Engaged', 'Qualified', 'Customer'];
  const [dragId, setDragId] = useStateA(null);
  return (
    <div className="nx-kanban">
      {cols.map(c => {
        const items = rows.filter(r => r.status === c);
        return (
          <div key={c} className="nx-kanban-col" onDragOver={e => e.preventDefault()} onDrop={() => { if (dragId) { onMove(dragId, c); setDragId(null); } }}>
            <div className="nx-kanban-head">
              <span className="nx-kanban-name">{c}</span>
              <span className="nx-kanban-count">{items.length}</span>
              <span style={{ flex: 1 }} />
              <IconBtn icon="Plus" size={13} />
            </div>
            <div className="nx-kanban-body">
              {items.map(p => (
                <button key={p.id} className="nx-kanban-card" draggable onDragStart={() => setDragId(p.id)} onClick={() => onOpen(p)}>
                  <div className="nx-kanban-card-head">
                    <Avatar name={p.name} tone={p.tone} size={22} />
                    <div className="nx-kanban-card-meta">
                      <span className="nx-kanban-card-title">{p.name}</span>
                      <span className="nx-kanban-card-sub">{p.role}</span>
                    </div>
                  </div>
                  <div className="nx-kanban-card-row">
                    <span className="nx-link-cell">{p.company}</span>
                    <Score value={p.score} />
                  </div>
                  <div className="nx-kanban-card-row">
                    {p.tags.slice(0, 2).map(t => <span key={t} className="nx-tag">{t}</span>)}
                    <span style={{ flex: 1 }} />
                    <span className="nx-muted nx-mini">{p.lastActivity}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Shared RecordCalendar — used by People, Companies, Deals ── */
const MONTH_LABELS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_ABBRS    = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const RecordCalendar = ({ rows, getSlot, getDot, getLabel, getTooltip, onOpen, initialAnchor }) => {
  const today = new Date();
  const [anchor, setAnchor] = useStateA(initialAnchor ?? new Date(today.getFullYear(), today.getMonth(), 1));
  const year  = anchor.getFullYear();
  const month = anchor.getMonth();

  // Day-of-week offset for 1st of the month (Mon-based: Mon=0 … Sun=6)
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7;

  const prevMonth = () => setAnchor(new Date(year, month - 1, 1));
  const nextMonth = () => setAnchor(new Date(year, month + 1, 1));
  const goToday   = () => setAnchor(new Date(today.getFullYear(), today.getMonth(), 1));
  const isToday   = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div className="nx-calendar nx-rec-calendar">
      {/* Navigation header */}
      <div className="nx-cal-nav">
        <button className="nx-cal-nav-btn" onClick={prevMonth}><NexusIcon name="ChevronLeft" size={14} /></button>
        <span className="nx-cal-nav-title">{MONTH_LABELS[month]} {year}</span>
        <button className="nx-cal-nav-btn" onClick={nextMonth}><NexusIcon name="ChevronRight" size={14} /></button>
        <button className="nx-cal-nav-today" onClick={goToday}>Today</button>
      </div>
      {/* Day-of-week header */}
      <div className="nx-cal-head">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <span key={d}>{d}</span>)}
      </div>
      {/* Grid */}
      <div className="nx-cal-grid">
        {Array.from({ length: totalCells }).map((_, i) => {
          const dayNum = i - firstDow + 1;
          const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
          const items = inMonth ? rows.filter(r => getSlot(r, dayNum, month, year)).slice(0, 3) : [];
          const overflow = inMonth ? Math.max(0, rows.filter(r => getSlot(r, dayNum, month, year)).length - 3) : 0;
          return (
            <div key={i} className={'nx-cal-cell' + (!inMonth ? ' is-out' : '') + (isToday(dayNum) ? ' is-today' : '')}>
              <span className={'nx-cal-day' + (isToday(dayNum) ? ' is-today' : '')}>{inMonth ? dayNum : ''}</span>
              {items.map(r => (
                <button key={r.id} className="nx-cal-event" onClick={() => onOpen && onOpen(r)} title={getTooltip(r)}>
                  <span className="nx-cal-event-dot" style={{ background: getDot(r) }} />
                  <span className="nx-cal-event-text">{getLabel(r)}</span>
                </button>
              ))}
              {overflow > 0 && <span className="nx-cal-overflow">+{overflow} more</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
window.RecordCalendar = RecordCalendar;

const PeopleCalendar = ({ rows, onOpen }) => {
  const store = useStore();
  return (
    <RecordCalendar
      rows={rows}
      onOpen={onOpen || (r => store.openDrawer(r))}
      getSlot={(r, d, month, year) => {
        // lastActivity is formatted as DD/MM/YYYY (en-IN locale) by normPerson
        if (!r.lastActivity) return false;
        const parts = r.lastActivity.split('/');
        if (parts.length !== 3) return false;
        const [dd, mm, yyyy] = parts.map(Number);
        return dd === d && mm - 1 === month && yyyy === year;
      }}
      getDot={(r) => r.tone || 'var(--nx-accent)'}
      getLabel={(r) => r.name}
      getTooltip={(r) => r.name + ' · ' + (r.role || '') + ' · ' + (r.company || '')}
    />
  );
};

window.PeopleView = PeopleView;

/* =========================================================
   COMPANIES VIEW
   ========================================================= */
const CompaniesView = () => {
  const store = useStore();
  const allCompanies = store.companies;
  const toast = useToast();
  const [activeView, setActiveView] = useStateA('all');
  const [editing, setEditing] = useStateA(null);
  const [cfPickerOpen, setCfPickerOpen] = useStateA(false);
  const [cfFilterPanelOpen, setCfFilterPanelOpen] = useStateA(false);
  const [savedViewsOpen, setSavedViewsOpen] = useStateA(false);
  const [activeViewId, setActiveViewId] = useStateA(null); // LV-03
  const fs = useFilters('companies', {});
  const ts = useTableState('companies', allCompanies, { field: 'updatedAt', dir: 'desc' });

  const companiesColumns = useColumns('companies'); // LV-07: unified ordered columns
  const companiesCfDefs = store.customFieldDefs?.companies || [];
  const companiesCfValues = store.cfValues?.companies || {};

  const dealCountByCompany = useMemoA(() => {
    const m = {};
    (store.deals || []).forEach(d => { if (d.companyId) m[d.companyId] = (m[d.companyId] || 0) + 1; });
    return m;
  }, [store.deals]);

  const filtered = useMemoA(() => {
    let list = allCompanies;
    if (activeView === 'enterprise') list = list.filter(r => r.lifecycle === 'Enterprise');
    if (activeView === 'accounts') list = list.filter(r => r.lifecycle === 'Account');
    if (fs.filters.industry) list = list.filter(c => c.industry === fs.filters.industry);
    if (fs.filters.lifecycle) list = list.filter(c => c.lifecycle === fs.filters.lifecycle);
    if (fs.filters.location) list = list.filter(c => c.location === fs.filters.location);
    if (fs.filters.owner) list = list.filter(c => c.owner === fs.filters.owner);
    if (fs.filters.scoreBand) list = list.filter(c => scoreBandMatches(c.score, fs.filters.scoreBand));
    if (fs.search) {
      const q = fs.search.toLowerCase();
      list = list.filter(c => (c.name + c.domain + c.industry + c.owner + c.location).toLowerCase().includes(q));
    }
    list = list.map(c => ({ ...c, deals: dealCountByCompany[c.id] ?? 0 }));
    list = applyCfFiltersClient(list, fs.filters, companiesCfValues);
    return ts.applySort(list);
  }, [allCompanies, activeView, fs.filters, fs.search, ts, dealCountByCompany, companiesCfValues]);

  const updateField = (id, field, value) => store.update('companies', id, { [field]: value });

  const handleExport = async () => {
    const ids = ts.selected.size ? new Set(ts.selected) : null;
    const rows = ids ? filtered.filter(c => ids.has(c.id)) : filtered;
    if (!rows.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export', desc: 'Adjust filters and try again.' }); return; }
    const { cols, hadCfError } = await window._prepareCfExport('companies', rows, store);
    window._download('companies-export.csv', 'text/csv;charset=utf-8;', window._toCSV(rows, cols));
    if (hadCfError) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Exported ' + rows.length + ' rows', desc: 'Custom field values could not be loaded and were left blank.' });
    } else {
      toast({ tone: 'success', icon: 'Download', title: 'Exported ' + rows.length + ' rows' });
    }
  };

  const allSelected = filtered.length > 0 && filtered.every(r => ts.selected.has(r.id));
  const someSelected = ts.selected.size > 0 && !allSelected;
  const checkAllRef = React.useRef(null);
  useEffectA(() => {
    if (checkAllRef.current) checkAllRef.current.indeterminate = someSelected;
  }, [someSelected]);
  const industries = [...new Set(allCompanies.map(c => c.industry).filter(Boolean))];
  const locations = [...new Set(allCompanies.map(c => c.location).filter(Boolean))];
  const allCompanyOwners = useMemoA(() => [...new Set(allCompanies.map(c => c.owner).filter(Boolean))], [allCompanies]);
  const [view, setView] = useStateA('table');

  const [saveViewOpen, setSaveViewOpen] = useStateA(false);
  const [loadedAt,    setLoadedAt]    = useStateA(() => new Date());
  const [tickCount,   setTickCount]   = useStateA(0);
  const [compPage,    setCompPage]    = useStateA(1);

  const CO_PAGE_SIZE = 50;

  useEffectA(() => {
    if (allCompanies.length > 0) setLoadedAt(new Date());
  }, [allCompanies.length]);

  useEffectA(() => {
    if (companiesCfDefs.length > 0 && allCompanies.length > 0) {
      store.loadCfValues('companies', allCompanies.map(c => c.id));
    }
  }, [allCompanies, companiesCfDefs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffectA(() => {
    const id = setInterval(() => setTickCount(n => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffectA(() => {
    setCompPage(1);
  }, [activeView, fs.search, fs.filters.industry, fs.filters.lifecycle, fs.filters.location, fs.filters.owner, fs.filters.scoreBand]);

  const totalCoPages  = Math.max(1, Math.ceil(filtered.length / CO_PAGE_SIZE));
  const safeCoPage    = Math.min(compPage, totalCoPages);
  const pagedCompanies = useMemoA(
    () => filtered.slice((safeCoPage - 1) * CO_PAGE_SIZE, safeCoPage * CO_PAGE_SIZE),
    [filtered, safeCoPage],
  );

  const loadedLabel = useMemoA(() => {
    const secs = Math.floor((Date.now() - loadedAt) / 1000);
    if (secs < 60)   return 'just now';
    if (secs < 3600) return Math.floor(secs / 60) + ' min ago';
    return Math.floor(secs / 3600) + 'h ago';
  }, [loadedAt, tickCount]);

  const handleApplyView = (sv) => {
    writeViewToSession('companies', sv);
    fs.setFilters(sv.filters || {});
    fs.setSearch(sv.search || '');
    ts.setSort(sv.sort ?? null);
    store.setColumnsFromView('companies', sv.columns ?? null); // LV-03: in-memory only, no upsertDefault
    setActiveViewId(sv.id);
    setCompPage(1);
    ts.clear();
    setSavedViewsOpen(false);
    toast({ tone: 'success', icon: 'CheckCircle', title: 'View applied — ' + sv.name });
  };

  const handleRenameView = (view, newName) => {
    store.updateView(view.id, { name: newName });
    toast({ tone: 'success', icon: 'Edit', title: 'View renamed to “' + newName + '”' });
  };

  const handleOverwriteView = (view) => {
    store.updateView(view.id, {
      filters: { ...(fs.filters || {}), ...(fs.search ? { search: fs.search } : {}) },
      sort: ts.sort || undefined,
      columns: store.cfColumns?.companies ?? undefined,
    });
    toast({ tone: 'success', icon: 'RefreshCw', title: 'View updated — ' + view.name });
  };

  return (
    <div className="nx-view">
      <ModuleHeader
        title="Companies"
        count={filtered.length}
        tabs={[
          { id: 'all', label: 'All Companies', count: allCompanies.length },
          { id: 'enterprise', label: 'Enterprise', count: allCompanies.filter(r => r.lifecycle === 'Enterprise').length },
          { id: 'accounts', label: 'Accounts', count: allCompanies.filter(r => r.lifecycle === 'Account').length },
        ]}
        activeTab={activeView}
        onTab={(tab) => { setActiveView(tab); if (tab !== 'all') fs.setFilter('lifecycle', null); }}
        actions={
          <>
            <div className="nx-segment">
              <IconBtn icon="List" active={view === 'table'} onClick={() => { setView('table'); emitView('companies','table'); }} title="Table" />
              <IconBtn icon="LayoutGrid" active={view === 'kanban'} onClick={() => { setView('kanban'); emitView('companies','kanban'); }} title="Kanban" />
              <IconBtn icon="Calendar" active={view === 'calendar'} onClick={() => { setView('calendar'); emitView('companies','calendar'); }} title="Calendar" />
            </div>
            <ExportMenu type="companies" rows={ts.selected.size ? filtered.filter(c => ts.selected.has(c.id)) : filtered} />
            <ImportButton type="companies" />
            <div style={{ position: 'relative' }}>
              <IconBtn icon="Columns" title="Customize columns" active={cfPickerOpen} onClick={() => setCfPickerOpen(o => !o)} />
              {cfPickerOpen && <ColumnManager entity="companies" onClose={() => setCfPickerOpen(false)} />}
            </div>
            <IconBtn icon="RotateCcw" onClick={store.reloadWorkspace} title="Refresh data" />
            <Btn variant="secondary" icon="Bookmark" onClick={() => setSaveViewOpen(true)}>Save View</Btn>
            {saveViewOpen && (
              <SaveViewModal
                entity="companies"
                onSave={(name) => {
                  store.saveView('companies', name, fs.filters, fs.search, store.cfColumns?.companies ?? null, ts.sort ?? null);
                  setSaveViewOpen(false);
                }}
                onClose={() => setSaveViewOpen(false)}
              />
            )}
            <div style={{ position: 'relative' }}>
              <Btn variant="secondary" icon="Eye" active={savedViewsOpen} onClick={() => setSavedViewsOpen(o => !o)}>Views</Btn>
              {savedViewsOpen && <SavedViewsPanel entity="companies" onApply={handleApplyView} onRename={handleRenameView} onOverwrite={handleOverwriteView} onClose={() => setSavedViewsOpen(false)} />}
            </div>
            <Btn variant="primary" icon="Plus" onClick={() => store.openCreateForm('companies')}>Add Company</Btn>
          </>
        }
        filters={
          <>
            <SearchField
              value={fs.search}
              onChange={e => fs.setSearch(e.target.value)}
              onClear={() => fs.setSearch('')}
              placeholder="Search companies…"
              aria-label="Search companies"
              width={220}
            />
            <FilterChip icon="Briefcase" label="Industry" value={fs.filters.industry} options={industries} onChange={v => fs.setFilter('industry', v)} active={!!fs.filters.industry} />
            {activeView === 'all' && (
              <FilterChip icon="Tag" label="Lifecycle" value={fs.filters.lifecycle} options={['Lead', 'Account', 'Enterprise']} onChange={v => fs.setFilter('lifecycle', v)} active={!!fs.filters.lifecycle} />
            )}
            <FilterChip icon="Globe" label="Location" value={fs.filters.location} options={locations} onChange={v => fs.setFilter('location', v)} active={!!fs.filters.location} />
            <FilterChip icon="User" label="Owner" value={fs.filters.owner} options={allCompanyOwners} onChange={v => fs.setFilter('owner', v)} active={!!fs.filters.owner} />
            <FilterChip icon="TrendingUp" label="Score" value={fs.filters.scoreBand} options={SCORE_BAND_FILTER_OPTIONS.map(b => b.label)} onChange={v => fs.setFilter('scoreBand', v)} active={!!fs.filters.scoreBand} />
            {companiesCfDefs.filter(_cfIsFilterable).length > 0 && (
              <div style={{ position: 'relative' }}>
                <IconBtn icon="SlidersHorizontal" title="Custom field filters" active={Object.keys(fs.filters).some(k => k.startsWith('cf_'))} onClick={() => setCfFilterPanelOpen(o => !o)} />
                {cfFilterPanelOpen && <CfFilterPanel defs={companiesCfDefs} filters={fs.filters} setFilter={fs.setFilter} onClose={() => setCfFilterPanelOpen(false)} />}
              </div>
            )}
            <CfActiveFilterChips defs={companiesCfDefs} filters={fs.filters} setFilter={fs.setFilter} />
            {fs.activeCount > 0 && (
              <Btn variant="ghost" size="sm" icon="X" onClick={fs.clearAll}>Clear ({fs.activeCount})</Btn>
            )}
          </>
        }
      />

      {ts.selected.size > 0 && (
        <BulkBar count={ts.selected.size} type="companies" ids={[...ts.selected]} onClear={ts.clear} onExport={handleExport}
          extraActions={<>
            <Btn icon="UserPlus" variant="ghost" onClick={() => store.bulkUpdate('companies', [...ts.selected], { owner: window.__nx_auth?.user?.name || '' })}>Reassign</Btn>
            <Btn icon="Archive" variant="ghost" onClick={() => store.confirm({
              title: `Archive ${ts.selected.size} ${ts.selected.size === 1 ? 'company' : 'companies'}?`,
              description: 'These companies will be hidden from all views. Linked contacts and deals are not affected.',
              onConfirm: () => { [...ts.selected].forEach(id => store.update('companies', id, { archived: true })); ts.clear(); },
            })}>Archive</Btn>
          </>}
        />
      )}

      {view === 'table' && (
        <div className="nx-table-wrap">
          <div className="nx-table-scroll">
            <table className="nx-table" aria-label="Companies">
              <thead>
                <tr>
                  <th scope="col" className="nx-th-check"><input ref={checkAllRef} type="checkbox" className="nx-checkbox" checked={allSelected} onChange={() => ts.toggleAll(filtered)} aria-label="Select all companies" /></th>
                  <SortableTh icon="Building" sticky field="name" sort={ts.sort} onSort={ts.onSort}>Company</SortableTh>
                  {/* LV-07: unified columns (standard + CF, ordered) */}
                  {companiesColumns.map(col => col.isCf
                    ? <Th key={col.field} style={{ width: 150 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>{col.label}<span style={{ fontSize: 9, fontWeight: 700, color: 'var(--nx-accent)', background: 'var(--nx-accent-tint, rgba(99,102,241,.1))', borderRadius: 3, padding: '1px 4px' }}>CF</span></span></Th>
                    : col.sortField
                      ? <SortableTh key={col.field} icon={col.icon} field={col.sortField} sort={ts.sort} onSort={ts.onSort} style={{ width: col.colWidth }}>{col.label}</SortableTh>
                      : <Th key={col.field} icon={col.icon} style={{ width: col.colWidth }}>{col.label}</Th>
                  )}
                  <th scope="col" className="nx-th-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedCompanies.map(c => {
                  const isEditing = (field) => editing && editing.id === c.id && editing.field === field;
                  const rowCfValues = companiesCfValues[c.id] || {};
                  return (
                    <tr key={c.id} className={'nx-tr' + (ts.selected.has(c.id) ? ' is-checked' : '')}>
                      <td className="nx-td-check"><input type="checkbox" className="nx-checkbox" checked={ts.selected.has(c.id)} onChange={() => ts.toggleOne(c.id)} aria-label={`Select ${c.name || 'this company'}`} /></td>
                      <td className="is-sticky">
                        <button className="nx-row-name" onClick={() => store.openDrawer(c)}>
                          <span className="nx-domain-mark" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}>{(c.name || '?')[0].toUpperCase()}</span>
                          <span className="nx-row-name-text">{c.name}</span>
                          <NexusIcon name="ArrowUpRight" size={11} className="nx-row-arrow" style={{ flexShrink:0 }} />
                        </button>
                      </td>
                      {/* LV-07: switch-dispatch per unified column */}
                      {companiesColumns.map(col => {
                        if (col.isCf) return <td key={col.field}><CfTableCell fieldId={col.cfFieldId} defs={companiesCfDefs} raw={rowCfValues[col.cfFieldId] ?? null} /></td>;
                        switch (col.field) {
                          case 'domain':        return <EditableCell key="domain" value={c.domain} editing={isEditing('domain')} onStart={() => setEditing({ id: c.id, field: 'domain' })} onSave={(v) => { updateField(c.id, 'domain', v); setEditing(null); }} onCancel={() => setEditing(null)} mono />;
                          case 'industry':      return <td key="industry">{c.industry}</td>;
                          case 'employees':     return <td key="employees"><span className="nx-cell is-mono">{c.employees != null ? c.employees.toLocaleString() : '—'}</span></td>;
                          case 'revenue':       return <EditableCell key="revenue" value={c.revenue} editing={isEditing('revenue')} onStart={() => setEditing({ id: c.id, field: 'revenue' })} onSave={(v) => { updateField(c.id, 'revenue', v); setEditing(null); }} onCancel={() => setEditing(null)} mono />;
                          case 'location':      return <td key="location">{c.location}</td>;
                          case 'lifecycle':     return <td key="lifecycle"><Pill tone={c.lifecycle === 'Enterprise' ? 'purple' : c.lifecycle === 'Account' ? 'blue' : 'gray'}>{c.lifecycle}</Pill></td>;
                          case 'score':         return <td key="score"><Score value={c.score} /></td>;
                          case 'open_deals':    return <td key="open_deals"><span className="nx-cell is-mono">{dealCountByCompany[c.id] ?? 0}</span></td>;
                          case 'owner':         return <td key="owner"><span className="nx-owner-cell"><Avatar name={c.owner || ''} tone="var(--nx-accent)" size={18} /><span>{(c.owner || '').split(' ')[0] || '—'}</span></span></td>;
                          case 'last_activity': return <td key="last_activity"><span className="nx-muted">{c.lastActivity}</span></td>;
                          default:              return <td key={col.field} />;
                        }
                      })}
                      <td className="nx-td-end"><RowActions type="companies" record={c} /></td>
                    </tr>
                  );
                })}
                {store.loading && allCompanies.length === 0 && (
                  <tr><td colSpan={3 + companiesColumns.length}>
                    <div className="nx-empty" style={{ gap: 'var(--space-3)' }}>
                      <NexusIcon name="Loader" size={20} style={{ animation: 'nx-spin 1s linear infinite' }} />
                      <span>Loading companies…</span>
                    </div>
                  </td></tr>
                )}
                {store.loadError && !store.loading && (
                  <tr><td colSpan={3 + companiesColumns.length}>
                    <div className="nx-empty" style={{ flexDirection: 'column', gap: 'var(--space-3)' }}>
                      <NexusIcon name="AlertCircle" size={20} style={{ color: 'var(--nx-danger, #D93025)' }} />
                      <span>Failed to load companies. Check your connection and try again.</span>
                      <Btn size="sm" variant="secondary" icon="RotateCcw" onClick={store.reloadWorkspace}>Retry</Btn>
                    </div>
                  </td></tr>
                )}
                {!store.loading && !store.loadError && filtered.length === 0 && allCompanies.length === 0 && (
                  <tr><td colSpan={3 + companiesColumns.length}>
                    <EmptyState icon="Building" title="No companies yet." desc="Add your first company to get started." action={<Btn size="sm" variant="primary" icon="Plus" onClick={() => store.openCreateForm('companies')}>Add Company</Btn>} />
                  </td></tr>
                )}
                {!store.loading && !store.loadError && filtered.length === 0 && allCompanies.length > 0 && (
                  <tr><td colSpan={3 + companiesColumns.length}><EmptyState icon="Search" title="No companies match your search or filters." /></td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="nx-table-foot">
            <span>{filtered.length} of {allCompanies.length}</span>
            <span className="nx-foot-sep">·</span>
            <span>Updated {loadedLabel}</span>
            <span style={{ flex: 1 }} />
            <Btn variant="ghost" icon="ChevronLeft" disabled={safeCoPage <= 1} onClick={() => setCompPage(p => Math.max(1, p - 1))} />
            <span className="nx-page">Page {safeCoPage} of {totalCoPages}</span>
            <Btn variant="ghost" iconRight="ChevronRight" disabled={safeCoPage >= totalCoPages} onClick={() => setCompPage(p => Math.min(totalCoPages, p + 1))} />
          </div>
        </div>
      )}

      {view === 'kanban' && (
        <CompaniesKanban rows={filtered} onOpen={store.openDrawer} onMove={(id, lifecycle) => store.update('companies', id, { lifecycle })} />
      )}
      {view === 'calendar' && (
        <CompaniesCalendar rows={filtered} onOpen={store.openDrawer} />
      )}
    </div>
  );
};
window.CompaniesView = CompaniesView;

/* =========================================================
   DEALS VIEW
   ========================================================= */
const DealsView = () => {
  const store = useStore();
  const stages = store.pipeline || [];
  const wonId = stages.find(s => s.kind === 'WON')?.id ?? null;
  const allDeals = store.deals;
  const archivedDeals = store.archivedDeals || [];
  const companies = store.companies;
  const people = store.people || [];
  const toast = useToast();
  const can = window.__nx_rbac?.can || (() => true);
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;

  /* ── Pipeline picker — load list; switch reloads workspace stage map ── */
  const pipelines = store.pipelines;
  useEffectA(() => {
    if (!wsId) return;
    window.__nx_api.crmPipelines.list(wsId)
      .then(res => { const d = Array.isArray(res) ? res : (res?.data ?? []); store.setPipelines(d); })
      .catch(() => {});
  }, [wsId, store.loading]); // re-fetch after workspace reload so list stays fresh

  const activePipelineId = store.activePipelineId ?? null;
  const handlePipelineChange = (pipelineId) => {
    store.setActivePipeline(pipelineId); // sets ID + calls reloadWorkspace
  };

  const [view, setView] = useStateA('table');
  const [dragId, setDragId] = useStateA(null);
  const [activeView, setActiveView] = useStateA('all');
  const [saveViewOpen, setSaveViewOpen] = useStateA(false);
  const [loadedAt, setLoadedAt] = useStateA(() => new Date());
  const [tickCount, setTickCount] = useStateA(0);
  const [dealPage, setDealPage] = useStateA(1);
  const [terminalConfirm, setTerminalConfirm] = useStateA(null);
  const [lostReason, setLostReason] = useStateA('');
  const [cfPickerOpen, setCfPickerOpen] = useStateA(false);
  const [cfFilterPanelOpen, setCfFilterPanelOpen] = useStateA(false);
  const [savedViewsOpen, setSavedViewsOpen] = useStateA(false);
  const [activeViewId, setActiveViewId] = useStateA(null); // LV-03

  const dealsColumns = useColumns('deals'); // LV-07: unified ordered columns
  const dealsCfDefs = store.customFieldDefs?.deals || [];
  const dealsCfValues = store.cfValues?.deals || {};

  const DEAL_PAGE_SIZE = 50;
  const fs = useFilters('deals', {});
  const ts = useTableState('deals', allDeals, { field: 'updatedAt', dir: 'desc' });

  useEffectA(() => { if (allDeals.length > 0) setLoadedAt(new Date()); }, [allDeals.length]);
  useEffectA(() => {
    const id = setInterval(() => setTickCount(n => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  useEffectA(() => {
    if (dealsCfDefs.length > 0 && allDeals.length > 0) {
      store.loadCfValues('deals', allDeals.map(d => d.id));
    }
  }, [allDeals, dealsCfDefs.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffectA(() => { setDealPage(1); }, [activeView, fs.search, fs.filters.owner, fs.filters.priority, fs.filters.stage, fs.filters.company, fs.filters.person, fs.filters.valueRange, fs.filters.closeDate, fs.filters.createdDate, fs.filters.probability, fs.filters.score, fs.filters.tag, fs.filters.status]);

  const loadedLabel = useMemoA(() => {
    const secs = Math.floor((Date.now() - loadedAt) / 1000);
    if (secs < 60) return 'just now';
    if (secs < 3600) return Math.floor(secs / 60) + ' min ago';
    return Math.floor(secs / 3600) + 'h ago';
  }, [loadedAt, tickCount]);

  const filtered = useMemoA(() => {
    let list = allDeals;
    if (fs.filters.owner)     list = list.filter(d => d.owner === fs.filters.owner);
    if (fs.filters.priority)  list = list.filter(d => d.priority === fs.filters.priority);
    if (fs.filters.stage)     list = list.filter(d => d.stageId === fs.filters.stage);
    if (fs.filters.company)   list = list.filter(d => d.companyId === fs.filters.company);
    if (fs.filters.person)    list = list.filter(d => d.contactId === fs.filters.person);
    if (fs.filters.valueRange) {
      const vr = fs.filters.valueRange;
      list = list.filter(d => {
        if (vr === 'lt10k')   return d.value < 10000;
        if (vr === '10k50k')  return d.value >= 10000 && d.value < 50000;
        if (vr === '50k100k') return d.value >= 50000 && d.value < 100000;
        return d.value >= 100000;
      });
    }
    if (fs.filters.closeDate) {
      const tod = new Date(); tod.setHours(0, 0, 0, 0);
      const cd7  = new Date(tod); cd7.setDate(tod.getDate() + 7);
      const cd30 = new Date(tod); cd30.setDate(tod.getDate() + 30);
      const cf = fs.filters.closeDate;
      list = list.filter(d => {
        if (!d.closeDate) return false;
        const dt = new Date(d.closeDate); dt.setHours(0, 0, 0, 0);
        if (cf === 'overdue') return dt < tod;
        if (cf === 'today')   return dt.getTime() === tod.getTime();
        if (cf === 'next7')   return dt >= tod && dt <= cd7;
        return dt >= tod && dt <= cd30;
      });
    }
    if (fs.filters.createdDate) {
      const tod  = new Date(); tod.setHours(0, 0, 0, 0);
      const cr7  = new Date(tod); cr7.setDate(tod.getDate() - 7);
      const cr30 = new Date(tod); cr30.setDate(tod.getDate() - 30);
      const cf = fs.filters.createdDate;
      list = list.filter(d => {
        if (!d.createdAt) return false;
        const dt = new Date(d.createdAt); dt.setHours(0, 0, 0, 0);
        if (cf === 'today') return dt.getTime() === tod.getTime();
        if (cf === 'last7') return dt >= cr7 && dt <= tod;
        return dt >= cr30 && dt <= tod;
      });
    }
    if (fs.filters.probability) {
      const pf = fs.filters.probability;
      list = list.filter(d => {
        if (pf === 'p0')  return d.probability >= 0  && d.probability <= 25;
        if (pf === 'p25') return d.probability > 25  && d.probability <= 50;
        if (pf === 'p50') return d.probability > 50  && d.probability <= 75;
        return d.probability > 75;
      });
    }
    if (fs.filters.score) {
      const sf = fs.filters.score;
      list = list.filter(d => {
        if (sf === 's0')  return d.score >= 0  && d.score <= 25;
        if (sf === 's25') return d.score > 25  && d.score <= 50;
        if (sf === 's50') return d.score > 50  && d.score <= 75;
        return d.score > 75;
      });
    }
    if (fs.filters.tag)    list = list.filter(d => d.tags?.includes(fs.filters.tag));
    if (fs.filters.status) {
      const sf = fs.filters.status;
      list = list.filter(d => {
        const kind = stages.find(s => s.id === d.stageId)?.kind;
        if (sf === 'won')  return kind === 'WON';
        if (sf === 'lost') return kind === 'LOST';
        return kind !== 'WON' && kind !== 'LOST';
      });
    }
    if (fs.search) {
      const q = fs.search.toLowerCase();
      list = list.filter(d => (d.title + ' ' + (d.owner || '') + ' ' + (companies.find(co => co.id === d.companyId)?.name || '')).toLowerCase().includes(q));
    }
    list = applyCfFiltersClient(list, fs.filters, dealsCfValues);
    return ts.applySort(list);
  }, [allDeals, fs.filters, fs.search, ts, companies, stages, dealsCfValues]);

  const tabFiltered = useMemoA(() => {
    if (activeView === 'archived') return archivedDeals;
    let list = filtered;
    if (activeView === 'high') list = list.filter(d => d.priority === 'High');
    if (activeView === 'won')  list = list.filter(d => d.stageId === wonId);
    if (activeView === 'mine') list = list.filter(d => d.owner === (window.__nx_auth?.user?.name || ''));
    return list;
  }, [filtered, activeView, archivedDeals, wonId]);

  const totalPages  = Math.max(1, Math.ceil(tabFiltered.length / DEAL_PAGE_SIZE));
  const safePage    = Math.min(dealPage, totalPages);
  const pagedDeals  = useMemoA(
    () => tabFiltered.slice((safePage - 1) * DEAL_PAGE_SIZE, safePage * DEAL_PAGE_SIZE),
    [tabFiltered, safePage],
  );

  const allSelected = tabFiltered.length > 0 && tabFiltered.every(d => ts.selected.has(d.id));
  const someSelected = ts.selected.size > 0 && !allSelected;
  const checkAllRef = React.useRef(null);
  useEffectA(() => { if (checkAllRef.current) checkAllRef.current.indeterminate = someSelected; }, [someSelected]);

  const totalValue = filtered.reduce((s, d) => s + d.value, 0);
  const wonValue   = filtered.filter(d => d.stageId === wonId).reduce((s, d) => s + d.value, 0);
  const fmt = (n) => '$' + (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : n);

  const handleApplyDealView = (sv) => {
    writeViewToSession('deals', sv);
    fs.setFilters(sv.filters || {});
    fs.setSearch(sv.search || '');
    ts.setSort(sv.sort ?? null);
    store.setColumnsFromView('deals', sv.columns ?? null); // LV-03: in-memory only, no upsertDefault
    setActiveViewId(sv.id);
    setDealPage(1);
    ts.clear();
    setSavedViewsOpen(false);
    toast({ tone: 'success', icon: 'CheckCircle', title: 'View applied — ' + sv.name });
  };

  const handleRenameDealView = (view, newName) => {
    store.updateView(view.id, { name: newName });
    toast({ tone: 'success', icon: 'Edit', title: 'View renamed to “' + newName + '”' });
  };

  const handleOverwriteDealView = (view) => {
    store.updateView(view.id, {
      filters: { ...(fs.filters || {}), ...(fs.search ? { search: fs.search } : {}) },
      sort: ts.sort || undefined,
      columns: store.cfColumns?.deals ?? undefined,
    });
    toast({ tone: 'success', icon: 'RefreshCw', title: 'View updated — ' + view.name });
  };

  const cancelTerminal = () => { setTerminalConfirm(null); setLostReason(''); };
  const confirmTerminalMove = () => {
    if (!terminalConfirm) return;
    const patch = { stageId: terminalConfirm.stage.id };
    if (terminalConfirm.stage.kind === 'LOST' && lostReason.trim()) patch.lostReason = lostReason.trim();
    store.update('deals', terminalConfirm.dealId, patch);
    cancelTerminal();
  };

  const canStage = can('crm.deals.stage') || can('crm.manage');

  const onDrop = (stageId) => {
    if (!dragId) return;
    if (!canStage) {
      toast({ tone: 'warning', icon: 'Lock', title: 'No permission', desc: 'You do not have permission to move deals between stages.' });
      setDragId(null);
      return;
    }
    const targetStage = stages.find(s => s.id === stageId);
    if (targetStage?.kind === 'WON' || targetStage?.kind === 'LOST') {
      setTerminalConfirm({ dealId: dragId, stage: targetStage });
      setDragId(null);
      return;
    }
    store.update('deals', dragId, { stageId });
    setDragId(null);
  };

  const canCreate = can('crm.deals.create') || can('crm.manage');

  return (
    <div className="nx-view">

      {terminalConfirm && (
        <Modal
          title={terminalConfirm.stage.kind === 'WON' ? 'Mark Deal as Won?' : 'Mark Deal as Lost?'}
          icon={terminalConfirm.stage.kind === 'WON' ? 'CheckCircle' : 'XCircle'}
          onClose={cancelTerminal}
          width={420}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={cancelTerminal}>Cancel</Btn>
              <Btn variant={terminalConfirm.stage.kind === 'WON' ? 'primary' : 'danger'} onClick={confirmTerminalMove}>
                {terminalConfirm.stage.kind === 'WON' ? 'Mark Won' : 'Mark Lost'}
              </Btn>
            </div>
          }
        >
          <p style={{ color: 'var(--nx-text-muted)', fontSize: 13, marginBottom: 0 }}>
            {terminalConfirm.stage.kind === 'WON'
              ? 'This will close the deal as Won. You can reopen it later from the deal drawer.'
              : 'This will close the deal as Lost. You can reopen it later from the deal drawer.'}
          </p>
          {terminalConfirm.stage.kind === 'LOST' && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: 'var(--nx-text-muted)' }}>Reason (optional)</label>
              <input
                className="nx-input"
                placeholder="e.g. Budget, Timing, Competitor…"
                value={lostReason}
                onChange={e => setLostReason(e.target.value)}
                autoFocus
              />
            </div>
          )}
        </Modal>
      )}

      <ModuleHeader
        title="Deals"
        count={tabFiltered.length}
        actions={
          <>
            {window.PipelinePicker && (
              <window.PipelinePicker
                activePipelineId={activePipelineId}
                pipelines={pipelines}
                onChange={handlePipelineChange}
              />
            )}
            <div className="nx-segment">
              <IconBtn icon="List"       active={view === 'table'}    onClick={() => { setView('table');    emitView('deals','table');    }} title="List" />
              <IconBtn icon="LayoutGrid" active={view === 'kanban'}   onClick={() => { setView('kanban');   emitView('deals','kanban');   }} title="Kanban" />
              <IconBtn icon="Calendar"   active={view === 'calendar'} onClick={() => { setView('calendar'); emitView('deals','calendar'); }} title="Calendar" />
            </div>
            <IconBtn icon="RotateCcw" title="Refresh" onClick={store.reloadWorkspace} />
            <ExportMenu type="deals" rows={tabFiltered} />
            <ImportButton type="deals" />
            {/* LV-07: Column Manager — disabled/hidden in Kanban mode */}
            <div style={{ position: 'relative' }}>
              <IconBtn icon="Columns" title={view === 'kanban' ? 'Switch to table view to manage columns' : 'Customize columns'} active={cfPickerOpen && view !== 'kanban'} onClick={() => { if (view !== 'kanban') setCfPickerOpen(o => !o); }} style={view === 'kanban' ? { opacity: 0.4, pointerEvents: 'none' } : {}} />
              {cfPickerOpen && view !== 'kanban' && <ColumnManager entity="deals" onClose={() => setCfPickerOpen(false)} />}
            </div>
            <Btn variant="secondary" icon="Bookmark" onClick={() => setSaveViewOpen(true)}>Save View</Btn>
            {saveViewOpen && (
              <SaveViewModal
                entity="deals"
                onSave={(name) => {
                  store.saveView('deals', name, fs.filters, fs.search, store.cfColumns?.deals ?? null, ts.sort ?? null);
                  setSaveViewOpen(false);
                }}
                onClose={() => setSaveViewOpen(false)}
              />
            )}
            <div style={{ position: 'relative' }}>
              <Btn variant="secondary" icon="Eye" active={savedViewsOpen} onClick={() => setSavedViewsOpen(o => !o)}>Views</Btn>
              {savedViewsOpen && <SavedViewsPanel entity="deals" onApply={handleApplyDealView} onRename={handleRenameDealView} onOverwrite={handleOverwriteDealView} onClose={() => setSavedViewsOpen(false)} />}
            </div>
            {canCreate && <Btn variant="primary" icon="Plus" onClick={() => store.openCreateForm('deals')}>Add Deal</Btn>}
          </>
        }
        tabs={[
          { id: 'all',      label: 'All Deals',     count: filtered.length },
          { id: 'high',     label: 'High Priority', count: filtered.filter(d => d.priority === 'High').length },
          { id: 'won',      label: 'Won',           count: wonId ? filtered.filter(d => d.stageId === wonId).length : 0 },
          { id: 'mine',     label: 'My Deals',      count: filtered.filter(d => d.owner === (window.__nx_auth?.user?.name || '')).length },
          { id: 'archived', label: 'Archived',      count: archivedDeals.length },
        ]}
        activeTab={activeView}
        onTab={v => { setActiveView(v); setDealPage(1); }}
        filters={
          <>
            <span className="nx-search-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flex: '0 0 auto' }}>
              <NexusIcon name="Search" size={13} style={{ position: 'absolute', left: 8, color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
              <input
                className="nx-search-input"
                style={{ paddingLeft: 28, width: 180 }}
                placeholder="Search deals…"
                value={fs.search}
                onChange={e => fs.setSearch(e.target.value)}
                aria-label="Search deals"
              />
              {fs.search && (
                <button type="button" style={{ position: 'absolute', right: 6, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }} onClick={() => fs.setSearch('')} aria-label="Clear search">
                  <NexusIcon name="X" size={11} />
                </button>
              )}
            </span>
            <FilterChip icon="User"        label="Owner"    value={fs.filters.owner}    options={store.memberNames?.length ? store.memberNames : [window.__nx_auth?.user?.name].filter(Boolean)} onChange={v => fs.setFilter('owner', v)}    active={!!fs.filters.owner} />
            <FilterChip icon="AlertCircle" label="Priority" value={fs.filters.priority} options={['High','Medium','Low']}                        onChange={v => fs.setFilter('priority', v)} active={!!fs.filters.priority} />
            <FilterChip icon="Tag"         label="Stage"    value={fs.filters.stage && stages.find(s => s.id === fs.filters.stage)?.name} options={stages.map(s => ({ value: s.id, label: s.name }))} onChange={v => fs.setFilter('stage', v)} active={!!fs.filters.stage} />
            <FilterChip icon="Building2"  label="Company"  value={fs.filters.company && companies.find(co => co.id === fs.filters.company)?.name} options={companies.map(co => ({ value: co.id, label: co.name }))} onChange={v => fs.setFilter('company', v)} active={!!fs.filters.company} />
            <FilterChip icon="UserCircle" label="Person"   value={fs.filters.person && people.find(p => p.id === fs.filters.person)?.name} options={people.map(p => ({ value: p.id, label: p.name }))} onChange={v => fs.setFilter('person', v)} active={!!fs.filters.person} />
            <FilterChip icon="DollarSign" label="Value"    value={fs.filters.valueRange && { lt10k: '< $10K', '10k50k': '$10K–$50K', '50k100k': '$50K–$100K', gt100k: '$100K+' }[fs.filters.valueRange]} options={[{ value: 'lt10k', label: '< $10K' }, { value: '10k50k', label: '$10K–$50K' }, { value: '50k100k', label: '$50K–$100K' }, { value: 'gt100k', label: '$100K+' }]} onChange={v => fs.setFilter('valueRange', v)} active={!!fs.filters.valueRange} />
            <FilterChip icon="Calendar"   label="Closes"   value={fs.filters.closeDate && { overdue: 'Overdue', today: 'Today', next7: 'Next 7 days', next30: 'Next 30 days' }[fs.filters.closeDate]} options={[{ value: 'overdue', label: 'Overdue' }, { value: 'today', label: 'Today' }, { value: 'next7', label: 'Next 7 days' }, { value: 'next30', label: 'Next 30 days' }]} onChange={v => fs.setFilter('closeDate', v)} active={!!fs.filters.closeDate} />
            <FilterChip icon="Clock"      label="Created"  value={fs.filters.createdDate && { today: 'Today', last7: 'Last 7 days', last30: 'Last 30 days' }[fs.filters.createdDate]} options={[{ value: 'today', label: 'Today' }, { value: 'last7', label: 'Last 7 days' }, { value: 'last30', label: 'Last 30 days' }]} onChange={v => fs.setFilter('createdDate', v)} active={!!fs.filters.createdDate} />
            <FilterChip icon="Percent"    label="Probability" value={fs.filters.probability && { p0: '0–25%', p25: '26–50%', p50: '51–75%', p75: '76–100%' }[fs.filters.probability]} options={[{ value: 'p0', label: '0–25%' }, { value: 'p25', label: '26–50%' }, { value: 'p50', label: '51–75%' }, { value: 'p75', label: '76–100%' }]} onChange={v => fs.setFilter('probability', v)} active={!!fs.filters.probability} />
            <FilterChip icon="Star"       label="Score"    value={fs.filters.score && { s0: '0–25', s25: '26–50', s50: '51–75', s75: '76–100' }[fs.filters.score]} options={[{ value: 's0', label: '0–25' }, { value: 's25', label: '26–50' }, { value: 's50', label: '51–75' }, { value: 's75', label: '76–100' }]} onChange={v => fs.setFilter('score', v)} active={!!fs.filters.score} />
            <FilterChip icon="Hash"       label="Tag"      value={fs.filters.tag} options={[...new Set(allDeals.flatMap(d => d.tags || []))].sort()} onChange={v => fs.setFilter('tag', v)} active={!!fs.filters.tag} />
            <FilterChip icon="Activity"   label="Status"   value={fs.filters.status && { open: 'Open', won: 'Won', lost: 'Lost' }[fs.filters.status]} options={[{ value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }]} onChange={v => fs.setFilter('status', v)} active={!!fs.filters.status} />
            {dealsCfDefs.filter(_cfIsFilterable).length > 0 && (
              <div style={{ position: 'relative' }}>
                <IconBtn icon="SlidersHorizontal" title="Custom field filters" active={Object.keys(fs.filters).some(k => k.startsWith('cf_'))} onClick={() => setCfFilterPanelOpen(o => !o)} />
                {cfFilterPanelOpen && <CfFilterPanel defs={dealsCfDefs} filters={fs.filters} setFilter={fs.setFilter} onClose={() => setCfFilterPanelOpen(false)} />}
              </div>
            )}
            <CfActiveFilterChips defs={dealsCfDefs} filters={fs.filters} setFilter={fs.setFilter} />
            {fs.activeCount > 0 && <Btn variant="ghost" size="sm" icon="X" onClick={fs.clearAll}>Clear ({fs.activeCount})</Btn>}
            <span className="nx-muted nx-mini" style={{ marginLeft: 'auto' }}>{fmt(totalValue)} total · {fmt(wonValue)} won</span>
          </>
        }
      />

      {view === 'kanban' && (
        <div className="nx-pipeline">
          {store.loading && allDeals.length === 0 && (
            <div className="nx-empty" style={{ width: '100%', padding: '60px 0' }}>
              <NexusIcon name="Loader" size={20} style={{ animation: 'nx-spin 1s linear infinite' }} /><span>Loading deals…</span>
            </div>
          )}
          {!store.loading && stages.map(stage => {
            const stageDeals = tabFiltered.filter(d => d.stageId === stage.id);
            const stageValue = stageDeals.reduce((s, d) => s + d.value, 0);
            let colAccent = stage.tone;
            if (stage.kind === 'WON')  colAccent = 'var(--nx-success, #16a34a)';
            if (stage.kind === 'LOST') colAccent = 'var(--nx-danger, #dc2626)';
            const colCls = 'nx-pipe-col' + (stage.kind === 'WON' ? ' is-won-col' : '') + (stage.kind === 'LOST' ? ' is-lost-col' : '');
            return (
              <div key={stage.id} className={colCls} style={{ borderTopColor: colAccent }} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(stage.id)}>
                <div className="nx-pipe-head">
                  <span className="nx-pipe-stage-tone" style={{ background: colAccent }} />
                  <span className="nx-pipe-stage-name">{stage.name}</span>
                  <span className="nx-pipe-stage-count">{stageDeals.length}</span>
                  <span style={{ flex: 1 }} />
                  <span className="nx-pipe-stage-value">{fmt(stageValue)}</span>
                </div>
                <div className="nx-pipe-body">
                  {stageDeals.length === 0 && (
                    <div style={{ padding: '16px 8px', textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No deals</div>
                  )}
                  {stageDeals.map(d => {
                    const co = companies.find(x => x.id === d.companyId);
                    const daysInStage = d.stageEnteredAt
                      ? Math.max(0, (Date.now() - new Date(d.stageEnteredAt).getTime()) / 86400000)
                      : d.age;
                    const slaBreached = stage.slaHours != null && daysInStage > stage.slaHours / 24;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        className={'nx-deal-card' + (dragId === d.id ? ' is-dragging' : '') + (slaBreached ? ' is-sla-breached' : '')}
                        draggable
                        onDragStart={() => setDragId(d.id)}
                        onClick={() => store.openDrawer(d)}
                        aria-label={'Open deal: ' + d.title}
                      >
                        <div className="nx-deal-row">
                          <span className="nx-deal-title">{d.title}</span>
                          {d.priority === 'High' && <span className="nx-deal-flag" title="High priority">!</span>}
                          {slaBreached && <NexusIcon name="AlertTriangle" size={11} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} title="SLA breached" />}
                        </div>
                        {co && (
                          <div className="nx-deal-row nx-deal-meta">
                            <span className="nx-domain-mark" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', width: 18, height: 18, fontSize: 9 }}>{co.name[0]}</span>
                            <span className="nx-deal-company">{co.name}</span>
                          </div>
                        )}
                        <div className="nx-deal-row nx-deal-foot">
                          <span className="nx-deal-value">{fmt(d.value)}</span>
                          <span style={{ flex: 1 }} />
                          {d.alerts && d.alerts.length > 0 && <DealAlertChips alerts={d.alerts} size="xs" />}
                          <Avatar name={d.owner} tone="var(--nx-accent)" size={18} />
                          <span className="nx-muted nx-mini">{d.closeDate}</span>
                        </div>
                        <div className="nx-deal-progress"><span style={{ width: stage.prob + '%', background: colAccent }} /></div>
                      </button>
                    );
                  })}
                  {canCreate && (
                    <button type="button" className="nx-pipe-add" onClick={() => store.openCreateForm('deals', { stageId: stage.uuid || stage.id, pipelineId: stage.pipelineId })}>
                      <NexusIcon name="Plus" size={12} /><span>Add Deal</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {allDeals.length >= 500 && (
        <div className="nx-banner nx-banner-warning" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'var(--nx-warning-tint, #fefce8)', borderBottom: '1px solid var(--nx-warning, #ca8a04)', fontSize: 12 }}>
          <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-warning, #ca8a04)', flexShrink: 0 }} />
          <span>Showing the first 500 deals. Additional records exist but are not loaded. Use filters to narrow results or contact your admin to increase the limit.</span>
        </div>
      )}

      {view === 'table' && (
        <div className="nx-table-wrap">
          {ts.selected.size > 0 && (
            <BulkBar count={ts.selected.size} type="deals" ids={[...ts.selected]} onClear={ts.clear}
              onExport={async () => {
                const rows = [...ts.selected].map(id => allDeals.find(x => x.id === id)).filter(Boolean);
                if (!rows.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export' }); return; }
                const { cols, hadCfError } = await window._prepareCfExport('deals', rows, store);
                window._download('deals-bulk-export.csv', 'text/csv;charset=utf-8;', window._toCSV(rows, cols));
                if (hadCfError) {
                  toast({ tone: 'warning', icon: 'AlertCircle', title: 'Exported ' + rows.length + ' deals', desc: 'Custom field values could not be loaded and were left blank.' });
                } else {
                  toast({ tone: 'success', icon: 'Download', title: 'Exported ' + rows.length + ' deals' });
                }
              }}
            />
          )}
          <div className="nx-table-scroll">
            <table className="nx-table" aria-label="Deals">
              <thead><tr>
                <th scope="col" className="nx-th-check">
                  <input ref={checkAllRef} type="checkbox" className="nx-checkbox" checked={allSelected} onChange={() => ts.toggleAll(tabFiltered)} aria-label="Select all deals" />
                </th>
                <SortableTh icon="Target" sticky field="title" sort={ts.sort} onSort={ts.onSort} style={{ width: 260 }}>Deal</SortableTh>
                {/* LV-07: unified columns (standard + CF, ordered) */}
                {dealsColumns.map(col => col.isCf
                  ? <Th key={col.field} style={{ width: 150 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>{col.label}<span style={{ fontSize: 9, fontWeight: 700, color: 'var(--nx-accent)', background: 'var(--nx-accent-tint, rgba(99,102,241,.1))', borderRadius: 3, padding: '1px 4px' }}>CF</span></span></Th>
                  : col.sortField
                    ? <SortableTh key={col.field} icon={col.icon} field={col.sortField} sort={ts.sort} onSort={ts.onSort} style={{ width: col.colWidth }}>{col.label}</SortableTh>
                    : <Th key={col.field} icon={col.icon} style={{ width: col.colWidth }}>{col.label}</Th>
                )}
                <th scope="col" className="nx-th-end">Actions</th>
              </tr></thead>
              <tbody>
                {store.loading && allDeals.length === 0 && (
                  <tr><td colSpan={3 + dealsColumns.length}><div className="nx-empty"><NexusIcon name="Loader" size={20} style={{ animation: 'nx-spin 1s linear infinite' }} /><span>Loading deals…</span></div></td></tr>
                )}
                {store.loadError && !store.loading && (
                  <tr><td colSpan={3 + dealsColumns.length}>
                    <div className="nx-empty" style={{ flexDirection: 'column', gap: 'var(--space-3)' }}>
                      <NexusIcon name="AlertCircle" size={20} style={{ color: 'var(--nx-danger, #D93025)' }} />
                      <span>Failed to load deals. Check your connection and try again.</span>
                      <Btn size="sm" variant="secondary" icon="RotateCcw" onClick={store.reloadWorkspace}>Retry</Btn>
                    </div>
                  </td></tr>
                )}
                {!store.loading && !store.loadError && tabFiltered.length === 0 && allDeals.length === 0 && (
                  <tr><td colSpan={3 + dealsColumns.length}>
                    <EmptyState icon="Target" title="No deals yet." desc="Add your first deal to get started." action={canCreate && <Btn size="sm" variant="primary" icon="Plus" onClick={() => store.openCreateForm('deals')}>Add Deal</Btn>} />
                  </td></tr>
                )}
                {!store.loading && !store.loadError && tabFiltered.length === 0 && allDeals.length > 0 && (
                  <tr><td colSpan={3 + dealsColumns.length}><EmptyState icon="Search" title="No deals match your search or filters." /></td></tr>
                )}
                {pagedDeals.map(d => {
                  const stage = stages.find(s => s.id === d.stageId);
                  const co = companies.find(x => x.id === d.companyId);
                  let stageTone = 'blue';
                  if (stage?.kind === 'WON') { stageTone = 'green'; }
                  else if (stage?.kind === 'LOST') { stageTone = 'red'; }
                  const rowCfValues = dealsCfValues[d.id] || {};
                  return (
                    <tr key={d.id} className={'nx-tr' + (ts.selected.has(d.id) ? ' is-checked' : '')} onClick={() => store.openDrawer(d)}>
                      <td className="nx-td-check" onClick={e => { e.stopPropagation(); ts.toggleOne(d.id); }}>
                        <input type="checkbox" className="nx-checkbox" checked={ts.selected.has(d.id)} onChange={() => ts.toggleOne(d.id)} aria-label={'Select ' + d.title} />
                      </td>
                      <td className="is-sticky">
                        <button type="button" className="nx-row-name" onClick={e => { e.stopPropagation(); store.openDrawer(d); }}>
                          <span className="nx-domain-mark" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', width: 24, height: 24, fontSize: 11, flexShrink: 0 }}>{d.title?.[0]}</span>
                          <span className="nx-row-name-text">{d.title}</span>
                          <NexusIcon name="ArrowUpRight" size={11} className="nx-row-arrow" style={{ flexShrink: 0 }} />
                        </button>
                      </td>
                      {/* LV-07: switch-dispatch per unified column */}
                      {dealsColumns.map(col => {
                        if (col.isCf) return <td key={col.field}><CfTableCell fieldId={col.cfFieldId} defs={dealsCfDefs} raw={rowCfValues[col.cfFieldId] ?? null} /></td>;
                        switch (col.field) {
                          case 'company':    return <td key="company">{co && co.name}</td>;
                          case 'value':      return <td key="value"><span className="nx-cell is-mono"><strong>{fmt(d.value)}</strong></span></td>;
                          case 'stage':      return <td key="stage"><Pill tone={stageTone}>{stage?.name || '—'}</Pill></td>;
                          case 'priority':   return <td key="priority"><Pill tone={d.priority === 'High' ? 'red' : d.priority === 'Medium' ? 'amber' : 'gray'}>{d.priority}</Pill></td>;
                          case 'close_date': return <td key="close_date"><span className="nx-cell is-mono">{d.closeDate}</span></td>;
                          case 'age':        return <td key="age"><span className="nx-cell is-mono">{d.age != null ? d.age + 'd' : '—'}</span></td>;
                          case 'owner':      return <td key="owner"><span className="nx-owner-cell"><Avatar name={d.owner} tone="var(--nx-accent)" size={18} /><span>{(d.owner || '').split(' ')[0]}</span></span></td>;
                          case 'last_touch': return <td key="last_touch"><span className="nx-muted">{d.lastTouch}</span></td>;
                          default:           return <td key={col.field} />;
                        }
                      })}
                      <td className="nx-td-end"><RowActions type="deals" record={d} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="nx-table-foot">
            <span>{tabFiltered.length} of {allDeals.length}</span>
            <span className="nx-foot-sep">·</span>
            <span>Updated {loadedLabel}</span>
            <span style={{ flex: 1 }} />
            <Btn variant="ghost" icon="ChevronLeft" disabled={safePage <= 1} onClick={() => setDealPage(p => Math.max(1, p - 1))} />
            <span className="nx-page">Page {safePage} of {totalPages}</span>
            <Btn variant="ghost" iconRight="ChevronRight" disabled={safePage >= totalPages} onClick={() => setDealPage(p => Math.min(totalPages, p + 1))} />
          </div>
        </div>
      )}

      {view === 'calendar' && (
        <DealsCalendar rows={tabFiltered} onOpen={store.openDrawer} companies={companies} />
      )}
    </div>
  );
};
window.DealsView = DealsView;

/* =========================================================
   TASKS VIEW
   ========================================================= */
const TaskSkel = () => (
  <div className="nx-task-row" style={{ pointerEvents: 'none', gap: 10 }}>
    <div className="nx-skeleton" style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0 }} />
    <div className="nx-skeleton" style={{ flex: 1, height: 13, borderRadius: 3 }} />
    <div className="nx-skeleton" style={{ width: 48, height: 18, borderRadius: 10 }} />
    <div className="nx-skeleton" style={{ width: 64, height: 13, borderRadius: 3 }} />
  </div>
);

const TasksView = () => {
  const store = useStore();
  const tasks = store.tasks;
  const [filter,  setFilter]  = useStateA('all');
  const [sortBy,  setSortBy]  = useStateA(() => {
    try {
      const raw = sessionStorage.getItem('nx-sort-tasks');
      if (raw) { const p = JSON.parse(raw); if (p && typeof p.field === 'string' && p.field) return p.field; }
    } catch (_) {}
    return '';
  });
  const [sortDir, setSortDir] = useStateA(() => {
    try {
      const raw = sessionStorage.getItem('nx-sort-tasks');
      if (raw) { const p = JSON.parse(raw); if (p && (p.dir === 'asc' || p.dir === 'desc')) return p.dir; }
    } catch (_) {}
    return 'asc';
  });
  const [cfPickerOpen, setCfPickerOpen] = useStateA(false);
  const [cfFilterPanelOpen, setCfFilterPanelOpen] = useStateA(false);
  const [savedViewsOpen, setSavedViewsOpen] = useStateA(false);
  const [saveViewOpen, setSaveViewOpen] = useStateA(false);
  const [activeViewId, setActiveViewId] = useStateA(null); // LV-03
  const fs = useFilters('tasks', {});

  const toast = useToast();
  const tasksCfCols = useCfColumns('tasks');
  const tasksCfDefs = store.customFieldDefs?.tasks || [];
  const tasksCfValues = store.cfValues?.tasks || {};

  const toggle = (id) => {
    const t = tasks.find(x => x.id === id);
    if (!t) return;
    if (t.status === 'cancelled') { store.update('tasks', id, { done: false }); return; }
    store.update('tasks', id, { done: !t.done });
  };

  const filtered = useMemoA(() => {
    let list = tasks;
    // Tab filter
    if (filter === 'open') list = list.filter(t => t.status === 'open' || t.status === 'in_progress');
    if (filter === 'done') list = list.filter(t => t.done);
    // Chip filters — assignee key fixed (was 'owner')
    if (fs.filters.priority) list = list.filter(t => t.priority === fs.filters.priority);
    if (fs.filters.assignee) list = list.filter(t => t.assignee === fs.filters.assignee);
    // Due date chip filter
    if (fs.filters.dueDate) {
      const pd  = (s) => { if (!s) return null; const d = new Date(s + 'T00:00:00'); d.setHours(0,0,0,0); return d; };
      const now = new Date(); now.setHours(0,0,0,0);
      const eow = new Date(now); eow.setDate(now.getDate() + 7);
      if      (fs.filters.dueDate === 'Overdue')       list = list.filter(t => { const d = pd(t.dueDate); return d && d < now; });
      else if (fs.filters.dueDate === 'Due Today')      list = list.filter(t => { const d = pd(t.dueDate); return d && d.getTime() === now.getTime(); });
      else if (fs.filters.dueDate === 'Due This Week')  list = list.filter(t => { const d = pd(t.dueDate); return d && d >= now && d <= eow; });
      else if (fs.filters.dueDate === 'No Due Date')    list = list.filter(t => !t.dueDate);
    }
    // Search
    if (fs.search) {
      const q = fs.search.toLowerCase();
      list = list.filter(t => (t.title + (t.notes || '')).toLowerCase().includes(q));
    }
    list = applyCfFiltersClient(list, fs.filters, tasksCfValues);
    return list;
  }, [tasks, filter, fs.filters, fs.search, tasksCfValues]);

  // Client-side sort — applied after filtering, before grouping
  const sorted = useMemoA(() => {
    if (!sortBy) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    const PRI = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
    return [...filtered].sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case 'Title':        av = (a.title    || '').toLowerCase(); bv = (b.title    || '').toLowerCase(); break;
        case 'Status':       av = a.status    || '';                bv = b.status    || '';                break;
        case 'Priority':     av = PRI[a.priority] ?? 4;            bv = PRI[b.priority] ?? 4;             break;
        case 'Due Date':     av = a.dueDate   || '￿';         bv = b.dueDate   || '￿';          break;
        case 'Created Date': av = a.createdAt || '';                bv = b.createdAt || '';                break;
        case 'Last Updated': av = a.updatedAt || '';                bv = b.updatedAt || '';                break;
        default: return 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [filtered, sortBy, sortDir]);

  const _today    = new Date(); _today.setHours(0, 0, 0, 0);
  const _tom      = new Date(_today); _tom.setDate(_today.getDate() + 1);
  const _isSameDay = (dt, ref) => { if (!dt) return false; const d = new Date(dt + 'T00:00:00'); d.setHours(0,0,0,0); return d.getTime() === ref.getTime(); };
  const groups = {
    Today:     sorted.filter(t =>  _isSameDay(t.dueDate, _today)),
    Tomorrow:  sorted.filter(t =>  _isSameDay(t.dueDate, _tom)),
    Upcoming:  sorted.filter(t =>  t.dueDate && !_isSameDay(t.dueDate, _today) && !_isSameDay(t.dueDate, _tom)),
    'No Date': sorted.filter(t => !t.dueDate),
  };

  useEffectA(() => {
    if (tasksCfDefs.length > 0 && tasks.length > 0) {
      store.loadCfValues('tasks', tasks.map(t => t.id));
    }
  }, [tasks, tasksCfDefs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClearAll = () => { fs.clearAll(); setSortBy(''); setSortDir('asc'); };
  const activeCount = fs.activeCount + (sortBy ? 1 : 0);

  const handleExport = async () => {
    if (!sorted.length) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing to export' }); return; }
    const { cols, hadCfError } = await window._prepareCfExport('tasks', sorted, store);
    window._download('tasks-export.csv', 'text/csv;charset=utf-8;', window._toCSV(sorted, cols));
    if (hadCfError) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Exported ' + sorted.length + ' tasks', desc: 'Custom field values could not be loaded and were left blank.' });
    } else {
      toast({ tone: 'success', icon: 'Download', title: 'Exported ' + sorted.length + ' tasks' });
    }
  };

  const handleApplyTaskView = (sv) => {
    writeViewToSession('tasks', sv);
    fs.setFilters(sv.filters || {});
    fs.setSearch(sv.search || '');
    if (sv.sort?.field) { setSortBy(sv.sort.field); setSortDir(sv.sort.dir || 'asc'); }
    else { setSortBy(''); setSortDir('asc'); }
    store.setColumnsFromView('tasks', sv.columns ?? null); // LV-03: in-memory only, no upsertDefault
    setActiveViewId(sv.id);
    setSavedViewsOpen(false);
    toast({ tone: 'success', icon: 'CheckCircle', title: 'View applied — ' + sv.name });
  };

  const handleRenameTaskView = (view, newName) => {
    store.updateView(view.id, { name: newName });
    toast({ tone: 'success', icon: 'Edit', title: 'View renamed to “' + newName + '”' });
  };

  const handleOverwriteTaskView = (view) => {
    const taskSort = sortBy ? { field: sortBy, dir: sortDir || 'asc' } : undefined;
    store.updateView(view.id, {
      filters: { ...(fs.filters || {}), ...(fs.search ? { search: fs.search } : {}) },
      sort: taskSort,
      columns: store.cfColumns?.tasks ?? undefined,
    });
    toast({ tone: 'success', icon: 'RefreshCw', title: 'View updated — ' + view.name });
  };

  return (
    <div className="nx-view">
      <ModuleHeader
        title="Tasks"
        count={tasks.filter(t => !t.done && t.status !== 'cancelled').length}
        tabs={[
          { id: 'all',  label: 'All',       count: tasks.length },
          { id: 'open', label: 'Open',       count: tasks.filter(t => !t.done && t.status !== 'cancelled').length },
          { id: 'done', label: 'Completed',  count: tasks.filter(t => t.done).length },
        ]}
        activeTab={filter}
        onTab={setFilter}
        actions={
          <>
            <div style={{ position: 'relative' }}>
              <IconBtn icon="Columns" title="Customize columns" active={cfPickerOpen} onClick={() => setCfPickerOpen(o => !o)} />
              {cfPickerOpen && <CfColumnPicker entity="tasks" onClose={() => setCfPickerOpen(false)} />}
            </div>
            <ExportMenu type="tasks" rows={sorted} label="Export" />
            <Btn variant="secondary" icon="Bookmark" onClick={() => setSaveViewOpen(true)}>Save View</Btn>
            {saveViewOpen && (
              <SaveViewModal
                entity="tasks"
                onSave={(name) => {
                  const taskSort = sortBy ? { field: sortBy, dir: sortDir } : null;
                  store.saveView('tasks', name, fs.filters, fs.search, store.cfColumns?.tasks ?? null, taskSort);
                  setSaveViewOpen(false);
                }}
                onClose={() => setSaveViewOpen(false)}
              />
            )}
            <div style={{ position: 'relative' }}>
              <Btn variant="secondary" icon="Eye" active={savedViewsOpen} onClick={() => setSavedViewsOpen(o => !o)}>Views</Btn>
              {savedViewsOpen && <SavedViewsPanel entity="tasks" onApply={handleApplyTaskView} onRename={handleRenameTaskView} onOverwrite={handleOverwriteTaskView} onClose={() => setSavedViewsOpen(false)} />}
            </div>
            <Btn variant="primary" icon="Plus" onClick={() => store.openCreateForm('tasks')}>Add Task</Btn>
          </>
        }
        filters={
          <>
            <SearchField
              value={fs.search}
              onChange={e => fs.setSearch(e.target.value)}
              placeholder="Search tasks…"
              aria-label="Search tasks"
              width={200}
            />
            <FilterChip icon="AlertCircle" label="Priority" value={fs.filters.priority} options={['High', 'Medium', 'Low', 'Urgent']} onChange={v => fs.setFilter('priority', v)} active={!!fs.filters.priority} />
            <FilterChip icon="User" label="Assignee" value={fs.filters.assignee} options={store.memberNames?.length ? store.memberNames : [window.__nx_auth?.user?.name].filter(Boolean)} onChange={v => fs.setFilter('assignee', v)} active={!!fs.filters.assignee} />
            <FilterChip icon="Calendar" label="Due" value={fs.filters.dueDate} options={['Overdue', 'Due Today', 'Due This Week', 'No Due Date']} onChange={v => fs.setFilter('dueDate', v)} active={!!fs.filters.dueDate} />
            <FilterChip icon="Sort" label="Sort" value={sortBy || null} options={['Title', 'Status', 'Priority', 'Due Date', 'Created Date', 'Last Updated']} onChange={v => setSortBy(v || '')} active={!!sortBy} />
            {sortBy && (
              <button
                type="button"
                className="nx-chip"
                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                title={sortDir === 'asc' ? 'Ascending — click to reverse' : 'Descending — click to reverse'}
                style={{ gap: 4 }}
              >
                <NexusIcon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={11} />
                {sortDir === 'asc' ? 'Asc' : 'Desc'}
              </button>
            )}
            {tasksCfDefs.filter(_cfIsFilterable).length > 0 && (
              <div style={{ position: 'relative' }}>
                <IconBtn icon="SlidersHorizontal" title="Custom field filters" active={Object.keys(fs.filters).some(k => k.startsWith('cf_'))} onClick={() => setCfFilterPanelOpen(o => !o)} />
                {cfFilterPanelOpen && <CfFilterPanel defs={tasksCfDefs} filters={fs.filters} setFilter={fs.setFilter} onClose={() => setCfFilterPanelOpen(false)} />}
              </div>
            )}
            <CfActiveFilterChips defs={tasksCfDefs} filters={fs.filters} setFilter={fs.setFilter} />
            {activeCount > 0 && (
              <button className="nx-chip is-clear" onClick={handleClearAll}>
                <NexusIcon name="X" size={11} />Clear
              </button>
            )}
          </>
        }
      />
      <div className="nx-tasks-board">
        {/* Tasks uses a div/card layout (not a table) — "Actions" label right-aligned per architectural convention */}
        <div className="nx-task-col-head">Actions</div>
        {Object.entries(groups).map(([label, items]) => items.length === 0 ? null : (
          <div key={label} className="nx-task-group">
            <div className="nx-task-group-head">
              <span className="nx-task-group-name">{label}</span>
              <span className="nx-task-group-count">{items.length}</span>
            </div>
            {items.map(t => {
              const typeIcon = { email: 'Mail', call: 'Phone', doc: 'File', meeting: 'Calendar' }[t.type] || 'CheckSquare';
              const rowCfValues = tasksCfValues[t.id] || {};
              return (
                <div key={t.id} className={'nx-task-row' + (t.done ? ' is-done' : '') + (t.status === 'in_progress' ? ' is-in-progress' : '') + (t.status === 'cancelled' ? ' is-cancelled' : '')} style={{ cursor: 'pointer' }} onClick={() => store.openDrawer(t)}>
                  <button className="nx-check" onClick={e => { e.stopPropagation(); toggle(t.id); }}>{t.done && <NexusIcon name="Check" size={11} strokeWidth={2.5} />}</button>
                  <span className="nx-task-type"><NexusIcon name={typeIcon} size={13} /></span>
                  <span className="nx-task-title">{t.title}</span>
                  {t.notes && <span className="nx-task-related"><NexusIcon name="AlignLeft" size={11} />{t.notes}</span>}
                  <span style={{ flex: 1 }} />
                  {tasksCfCols.map(col => {
                    const rawVal = rowCfValues[col.cfFieldId] ?? null;
                    if (!rawVal && rawVal !== 0) return null;
                    return (
                      <span key={col.field} className="nx-cf-task-chip" title={col.label} style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 100 }}>
                        <CfTableCell fieldId={col.cfFieldId} defs={tasksCfDefs} raw={rawVal} />
                      </span>
                    );
                  })}
                  <Pill tone={t.done ? 'green' : t.status === 'in_progress' ? 'blue' : t.status === 'cancelled' ? 'red' : 'gray'}>{t.done ? 'Completed' : t.status === 'in_progress' ? 'In Progress' : t.status === 'cancelled' ? 'Cancelled' : 'Open'}</Pill>
                  <Pill tone={(t.priority === 'High' || t.priority === 'Urgent') ? 'red' : t.priority === 'Medium' ? 'amber' : 'gray'}>{t.priority}</Pill>
                  {t.dueDate && <span className="nx-task-due"><NexusIcon name="Clock" size={11} />{t.dueDate}</span>}
                  {(t.assignee || t.owner) && <Avatar name={t.assignee || t.owner} tone="var(--nx-accent)" size={20} />}
                  <RowActions type="tasks" record={t} />
                </div>
              );
            })}
          </div>
        ))}
        {store.loading && tasks.length === 0 && [0,1,2,3].map(i => <TaskSkel key={i} />)}
        {!store.loading && sorted.length === 0 && <div className="nx-empty"><NexusIcon name="CheckSquare" size={24} /><span>No tasks match your filters.</span></div>}
      </div>
    </div>
  );
};
window.TasksView = TasksView;

/* =========================================================
   CALENDAR VIEW — fully functional. Tasks ↔ events, week/month/day,
   real navigation, drag & drop reschedule, click-to-open.
   ========================================================= */
const CAL_TYPE_TONE = {
  call:    'var(--nx-tone-success)',
  email:   'var(--nx-tone-primary)',
  meeting: 'var(--nx-tone-rose)',
  doc:     'var(--nx-tone-amber)',
  note:    'var(--nx-accent)',
  default: 'var(--nx-accent)',
};
const CAL_TYPE_ICON = { call: 'Phone', email: 'Mail', meeting: 'Users', doc: 'FileText', note: 'StickyNote' };

const WEEKDAY_ABBR = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTH_NAMES  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay    = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const mondayOf   = (d) => { const x = startOfDay(d); const dow = (x.getDay()+6)%7; return addDays(x, -dow); };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);


const CalendarView = () => {
  const store = useStore();
  const toast = useToast();
  const [view, setView] = useStateA('week'); // week | month | day | agenda
  const [anchor, setAnchor] = useStateA(() => startOfDay(new Date()));
  const [dragId, setDragId] = useStateA(null);
  const [hoverKey, setHoverKey] = useStateA(null);
  const [priorityFilter, setPriorityFilter] = useStateA(null);
  const [assigneeFilter, setAssigneeFilter] = useStateA(null);

  // Compose events from tasks — parse ISO dueDate, apply active filters
  const events = useMemoA(() => {
    return (store.tasks || [])
      .filter(t => t.status !== 'cancelled')
      .filter(t => !priorityFilter || t.priority === priorityFilter)
      .filter(t => !assigneeFilter || t.assignee === assigneeFilter)
      .map(t => {
        if (!t.dueDate) return null;
        const at = new Date(t.dueDate + 'T09:00:00');
        if (isNaN(at.getTime())) return null;
        return {
          id:       t.id,
          title:    t.title,
          who:      t.assignee || '',
          at,
          date:     startOfDay(at),
          hour:     9,
          priority: t.priority,
          done:     !!t.done,
          status:   t.status,
          tone:     t.done ? 'var(--nx-success, #059669)' : t.status === 'in_progress' ? 'var(--nx-accent)' : CAL_TYPE_TONE.default,
        };
      }).filter(Boolean);
  }, [store.tasks, priorityFilter, assigneeFilter]);

  // ---- Range helpers per view ----
  const weekStart = useMemoA(() => mondayOf(anchor), [anchor]);
  const weekDays  = useMemoA(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const monthStart = useMemoA(() => startOfMonth(anchor), [anchor]);
  const monthGridStart = useMemoA(() => mondayOf(monthStart), [monthStart]);
  const monthCells = useMemoA(() => Array.from({ length: 42 }, (_, i) => addDays(monthGridStart, i)), [monthGridStart]);
  const dayHours = useMemoA(() => Array.from({ length: 12 }, (_, i) => 8 + i), []);

  const viewEventCount = useMemoA(() => {
    if (view === 'week')  return events.filter(e => weekDays.some(d => sameDay(d, e.date))).length;
    if (view === 'month') return events.filter(e => e.date >= monthGridStart && e.date < addDays(monthGridStart, 42)).length;
    if (view === 'day')   return events.filter(e => sameDay(e.date, anchor)).length;
    return events.length;
  }, [events, view, weekDays, monthGridStart, anchor]);

  const rangeLabel = useMemoA(() => {
    if (view === 'agenda') return 'Next 30 days';
    if (view === 'week') {
      const a = weekDays[0], b = weekDays[4];
      const sameMonth = a.getMonth() === b.getMonth();
      const mA = MONTH_NAMES[a.getMonth()].slice(0,3);
      const mB = MONTH_NAMES[b.getMonth()].slice(0,3);
      return sameMonth
        ? `${mA} ${a.getDate()} – ${b.getDate()}, ${b.getFullYear()}`
        : `${mA} ${a.getDate()} – ${mB} ${b.getDate()}, ${b.getFullYear()}`;
    }
    if (view === 'month') return `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`;
    return `${WEEKDAY_ABBR[anchor.getDay()]}, ${MONTH_NAMES[anchor.getMonth()].slice(0,3)} ${anchor.getDate()}, ${anchor.getFullYear()}`;
  }, [view, weekDays, anchor]);

  const nav = (dir) => {
    if (view === 'week') setAnchor(a => addDays(a, dir * 7));
    else if (view === 'month') setAnchor(a => new Date(a.getFullYear(), a.getMonth() + dir, 1));
    else setAnchor(a => addDays(a, dir));
  };
  const goToday = () => setAnchor(startOfDay(new Date()));

  // ---- Drag & drop ----
  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch (_) {}
  };
  const onDragOver = (key) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (hoverKey !== key) setHoverKey(key); };
  const onDragLeave = () => setHoverKey(null);
  const dropTo = (date, hour, keepHour) => {
    if (!dragId) return;
    const task = (store.tasks || []).find(t => t.id === dragId);
    if (!task) return;
    const next = new Date(date);
    next.setHours(hour, 0, 0, 0);
    store.update('tasks', dragId, { dueDate: next.toISOString().split('T')[0] });
    setDragId(null); setHoverKey(null);
  };

  const openEvent = (ev) => {
    const task = (store.tasks || []).find(t => t.id === ev.id);
    if (task) store.openDrawer(task);
  };

  // ---- Renderers ----
  const renderWeek = () => (
    <div className="nx-cal-week">
      <div className="nx-cal-week-head">
        <div />
        {weekDays.map(d => (
          <div key={d.toISOString()} className={'nx-cal-week-day' + (sameDay(d, new Date()) ? ' is-today' : '')}>
            <span className="nx-cal-week-dow">{WEEKDAY_ABBR[d.getDay()]}</span>
            <span className="nx-cal-week-num">{d.getDate()}</span>
          </div>
        ))}
      </div>
      <div className="nx-cal-week-grid" style={{ gridTemplateRows: `repeat(${dayHours.length}, 1fr)` }}>
        {dayHours.map((h, ri) => (
          <React.Fragment key={h}>
            <div className="nx-cal-week-hour">{(h % 12 || 12) + (h >= 12 ? ' PM' : ' AM')}</div>
            {weekDays.map((d, di) => {
              const key = `${di}-${h}`;
              return (
                <div
                  key={key}
                  className={'nx-cal-week-cell' + (hoverKey === key ? ' is-drop' : '')}
                  onDragOver={onDragOver(key)}
                  onDragLeave={onDragLeave}
                  onDrop={() => dropTo(d, h, false)}
                  onDoubleClick={() => {
                    const at = new Date(d); at.setHours(h, 0, 0, 0);
                    store.openCreateForm('tasks', { dueDate: at.toISOString().split('T')[0], priority: 'Medium' });
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
        {events.map(ev => {
          const di = weekDays.findIndex(d => sameDay(d, ev.date));
          if (di < 0) return null;
          const startIdx = Math.max(0, Math.floor(ev.hour) - dayHours[0]);
          if (startIdx >= dayHours.length) return null;
          return (
            <div
              key={ev.id}
              className={'nx-cal-week-event' + (ev.done ? ' is-done' : '') + (ev.status === 'in_progress' ? ' is-in-progress' : '') + (dragId === ev.id ? ' is-dragging' : '')}
              draggable
              onDragStart={onDragStart(ev.id)}
              onClick={() => openEvent(ev)}
              style={{
                gridColumnStart: di + 2,
                gridRowStart: startIdx + 1,
                gridRowEnd: `span 1`,
                '--ev-tone': ev.tone,
                background: 'color-mix(in srgb, var(--ev-tone) 12%, transparent)',
                borderLeftColor: ev.tone,
                color: ev.tone,
              }}>
              <span className="nx-cal-week-event-title">{ev.title}</span>
              <span className="nx-cal-week-event-who">{ev.who}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderMonth = () => (
    <div className="nx-cal-month-view">
      <div className="nx-cal-head">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <span key={d}>{d}</span>)}
      </div>
      <div className="nx-cal-grid nx-cal-month-grid">
        {monthCells.map((d, i) => {
          const isOut = d.getMonth() !== anchor.getMonth();
          const isToday = sameDay(d, new Date());
          const cellEvents = events.filter(e => sameDay(e.date, d));
          return (
            <div
              key={i}
              className={'nx-cal-cell' + (isOut ? ' is-out' : '') + (isToday ? ' is-today' : '') + (hoverKey === 'm' + i ? ' is-drop' : '')}
              onDragOver={onDragOver('m' + i)}
              onDragLeave={onDragLeave}
              onDrop={() => dropTo(d, 9, true)}
              onDoubleClick={() => {
                const at = new Date(d); at.setHours(9, 0, 0, 0);
                store.openCreateForm('tasks', { dueDate: at.toISOString().split('T')[0], priority: 'Medium' });
              }}>
              <span className="nx-cal-day">{d.getDate()}</span>
              {cellEvents.slice(0, 3).map(ev => (
                <span
                  key={ev.id}
                  className={'nx-cal-event' + (ev.done ? ' is-done' : '') + (ev.status === 'in_progress' ? ' is-in-progress' : '')}
                  draggable
                  onDragStart={onDragStart(ev.id)}
                  onClick={(e) => { e.stopPropagation(); openEvent(ev); }}
                  style={{ '--ev-tone': ev.tone, background: 'color-mix(in srgb, var(--ev-tone) 12%, transparent)', color: ev.tone }}>
                  <span className="nx-cal-event-dot" style={{ background: ev.tone }} />
                  {ev.title}
                </span>
              ))}
              {cellEvents.length > 3 && (
                <span className="nx-cal-event-more" onClick={(e) => { e.stopPropagation(); setView('day'); setAnchor(startOfDay(d)); }}>
                  +{cellEvents.length - 3} more
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderDay = () => {
    const dayEvents = events.filter(e => sameDay(e.date, anchor)).sort((a, b) => a.hour - b.hour);
    return (
      <div className="nx-cal-week nx-cal-day-view">
        <div className="nx-cal-week-head nx-cal-day-head">
          <div />
          <div className={'nx-cal-week-day' + (sameDay(anchor, new Date()) ? ' is-today' : '')}>
            <span className="nx-cal-week-dow">{WEEKDAY_ABBR[anchor.getDay()]}</span>
            <span className="nx-cal-week-num">{anchor.getDate()} {MONTH_NAMES[anchor.getMonth()].slice(0,3)}</span>
          </div>
          <div className="nx-cal-day-summary">
            <span className="nx-cal-day-count">{dayEvents.length}</span>
            <span className="nx-cal-day-count-label">{dayEvents.length === 1 ? 'event' : 'events'}</span>
          </div>
        </div>
        <div className="nx-cal-week-grid nx-cal-day-grid" style={{ gridTemplateRows: `repeat(${dayHours.length}, 1fr)` }}>
          {dayHours.map(h => (
            <React.Fragment key={h}>
              <div className="nx-cal-week-hour">{(h % 12 || 12) + (h >= 12 ? ' PM' : ' AM')}</div>
              <div
                className={'nx-cal-week-cell' + (hoverKey === 'd' + h ? ' is-drop' : '')}
                onDragOver={onDragOver('d' + h)}
                onDragLeave={onDragLeave}
                onDrop={() => dropTo(anchor, h, false)}
                onDoubleClick={() => {
                  const at = new Date(anchor); at.setHours(h, 0, 0, 0);
                  store.openCreateForm('tasks', { dueDate: at.toISOString().split('T')[0], priority: 'Medium' });
                }}
              />
              <div className="nx-cal-day-rail" />
            </React.Fragment>
          ))}
          {dayEvents.map(ev => {
            const startIdx = Math.max(0, Math.floor(ev.hour) - dayHours[0]);
            if (startIdx >= dayHours.length) return null;
            return (
              <div
                key={ev.id}
                className={'nx-cal-week-event nx-cal-day-event' + (ev.done ? ' is-done' : '') + (ev.status === 'in_progress' ? ' is-in-progress' : '') + (dragId === ev.id ? ' is-dragging' : '')}
                draggable
                onDragStart={onDragStart(ev.id)}
                onClick={() => openEvent(ev)}
                style={{
                  gridColumnStart: 2,
                  gridRowStart: startIdx + 1,
                  gridRowEnd: `span 1`,
                  background: ev.tone + '14',
                  borderLeftColor: ev.tone,
                  color: ev.tone,
                }}>
                <span className="nx-cal-week-event-title">{ev.title}</span>
                <span className="nx-cal-week-event-who">{ev.who}{ev.priority ? ' · ' + ev.priority : ''}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAgenda = () => {
    const todayStr  = new Date().toISOString().split('T')[0];
    const endStr    = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const applyF    = (list) => list
      .filter(t => !priorityFilter || t.priority === priorityFilter)
      .filter(t => !assigneeFilter || t.assignee === assigneeFilter);

    const overdueTasks  = applyF((store.tasks || []).filter(t =>
      t.dueDate && t.dueDate < todayStr && (t.status === 'open' || t.status === 'in_progress') && !t.archived));
    const upcomingTasks = applyF((store.tasks || []).filter(t =>
      t.dueDate && t.dueDate >= todayStr && t.dueDate <= endStr && (t.status === 'open' || t.status === 'in_progress') && !t.archived));
    const doneTasks     = applyF((store.tasks || []).filter(t => t.done && !t.archived)).slice(0, 20);

    const priTone = p => (p === 'High' || p === 'Urgent') ? 'red' : p === 'Medium' ? 'amber' : 'gray';
    const toggle  = (id) => { const t = (store.tasks || []).find(x => x.id === id); if (!t) return; if (t.status === 'cancelled') { store.update('tasks', id, { done: false }); return; } store.update('tasks', id, { done: !t.done }); };

    const AgendaSection = ({ label, items, accent }) => {
      if (!items.length) return null;
      return (
        <div className="nx-agenda-section">
          <div className="nx-agenda-section-head">
            <span className="nx-agenda-section-label" style={{ color: accent }}>{label}</span>
            <span className="nx-badge">{items.length}</span>
          </div>
          {items.map(t => (
            <div key={t.id} className={'nx-agenda-row' + (t.done ? ' is-done' : '') + (t.status === 'in_progress' ? ' is-in-progress' : '')}>
              <button className="nx-check" onClick={() => toggle(t.id)}>
                {t.done && <NexusIcon name="Check" size={11} strokeWidth={2.5} />}
              </button>
              <div className="nx-agenda-body">
                <span className="nx-agenda-title" role="button" tabIndex={0}
                  onClick={() => store.openDrawer(t)}
                  onKeyDown={e => e.key === 'Enter' && store.openDrawer(t)}>
                  {t.title}
                </span>
                <div className="nx-agenda-meta">
                  {t.dueDate   && <span className="nx-task-meta-chip"><NexusIcon name="Calendar" size={9} />{t.dueDate}</span>}
                  {t.assignee  && <span className="nx-task-meta-chip"><NexusIcon name="User" size={9} />{t.assignee.split(' ')[0]}</span>}
                </div>
              </div>
              <Pill tone={t.done ? 'green' : t.status === 'in_progress' ? 'blue' : t.status === 'cancelled' ? 'red' : 'gray'}>{t.done ? 'Completed' : t.status === 'in_progress' ? 'In Progress' : t.status === 'cancelled' ? 'Cancelled' : 'Open'}</Pill>
              <Pill tone={priTone(t.priority)}>{t.priority}</Pill>
            </div>
          ))}
        </div>
      );
    };

    const hasAny = overdueTasks.length + upcomingTasks.length + doneTasks.length > 0;
    return (
      <div className="nx-agenda-view">
        {!hasAny && <div className="nx-empty"><NexusIcon name="Calendar" size={24} /><span>No tasks in the next 30 days.</span></div>}
        <AgendaSection label="Overdue" items={overdueTasks} accent="var(--nx-danger, #DC2626)" />
        <AgendaSection label="Upcoming — next 30 days" items={upcomingTasks} accent="var(--nx-accent, #125AB0)" />
        <AgendaSection label="Recently completed" items={doneTasks} accent="var(--nx-success, #059669)" />
      </div>
    );
  };

  return (
    <div className="nx-view">
      <ModuleHeader
        title="Calendar"
        actions={
          <>
            {view !== 'agenda' && (
              <div className="nx-cal-nav-group">
                <IconBtn icon="ChevronLeft" onClick={() => nav(-1)} title="Previous" />
                <span className="nx-cal-month">{rangeLabel}</span>
                <IconBtn icon="ChevronRight" onClick={() => nav(1)} title="Next" />
                <Btn variant="secondary" size="sm" onClick={goToday}>Today</Btn>
              </div>
            )}
            {view === 'agenda' && (
              <span className="nx-cal-month">{rangeLabel}</span>
            )}
            <div className="nx-segment">
              <IconBtn icon="Columns"     active={view === 'week'}   onClick={() => setView('week')}   title="Week" />
              <IconBtn icon="LayoutGrid"  active={view === 'month'}  onClick={() => setView('month')}  title="Month" />
              <IconBtn icon="AlignLeft"   active={view === 'day'}    onClick={() => setView('day')}    title="Day" />
              <IconBtn icon="List"        active={view === 'agenda'} onClick={() => setView('agenda')} title="Agenda" />
            </div>
            <Btn variant="primary" icon="Plus" onClick={() => {
              const at = view === 'day' ? new Date(anchor) : new Date();
              if (view !== 'day') at.setHours(10, 0, 0, 0); else at.setHours(at.getHours(), 0, 0, 0);
              store.openCreateForm('tasks', { dueDate: at.toISOString().split('T')[0], priority: 'Medium' });
            }}>Add Task</Btn>
          </>
        }
        filters={
          <>
            <FilterChip icon="AlertCircle" label="Priority"
              value={priorityFilter}
              options={['High', 'Medium', 'Low', 'Urgent']}
              onChange={v => setPriorityFilter(v)}
              active={!!priorityFilter} />
            <FilterChip icon="User" label="Assignee"
              value={assigneeFilter}
              options={(store.memberNames || []).slice(0, 20)}
              onChange={v => setAssigneeFilter(v)}
              active={!!assigneeFilter} />
            {(priorityFilter || assigneeFilter) && (
              <button className="nx-chip is-clear" onClick={() => { setPriorityFilter(null); setAssigneeFilter(null); }}>
                <NexusIcon name="X" size={11} />Clear
              </button>
            )}
            <span className="nx-muted nx-mini" style={{ marginLeft: 'auto' }}>
              {viewEventCount} event{viewEventCount !== 1 ? 's' : ''}
            </span>
          </>
        }
      />
      {store.loading && store.tasks.length === 0 && (
        <div className="nx-cal-skeleton">
          {[0,1,2,3,4].map(i => (
            <div key={i} className="nx-skel" style={{ height: 40, marginBottom: 8, width: `${70 + (i % 3) * 10}%`, opacity: 0.6 - i * 0.08 }} />
          ))}
        </div>
      )}
      {!store.loading && view === 'week'   && renderWeek()}
      {!store.loading && view === 'month'  && renderMonth()}
      {!store.loading && view === 'day'    && renderDay()}
      {view === 'agenda' && renderAgenda()}
    </div>
  );
};
window.CalendarView = CalendarView;

/* =========================================================
   COMPANIES — Kanban (by lifecycle) + Calendar (by lastActivity)
   ========================================================= */
const CompaniesKanban = ({ rows, onOpen, onMove }) => {
  const cols = ['Lead', 'Account', 'Enterprise'];
  const [dragId, setDragId] = useStateA(null);
  return (
    <div className="nx-kanban">
      {cols.map(c => {
        const items = rows.filter(r => r.lifecycle === c);
        const totalDeals = items.reduce((s, r) => s + (r.deals || 0), 0);
        return (
          <div key={c} className="nx-kanban-col" onDragOver={e => e.preventDefault()} onDrop={() => { if (dragId) { onMove(dragId, c); setDragId(null); } }}>
            <div className="nx-kanban-head">
              <span className="nx-kanban-name">{c}</span>
              <span className="nx-kanban-count">{items.length}</span>
              <span style={{ flex: 1 }} />
              <span className="nx-muted nx-mini">{totalDeals} deals</span>
            </div>
            <div className="nx-kanban-body">
              {items.map(co => (
                <button key={co.id} className="nx-kanban-card" draggable onDragStart={() => setDragId(co.id)} onClick={() => onOpen(co)}>
                  <div className="nx-kanban-card-head">
                    <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 11 }}>{(co.name || '?')[0]}</span>
                    <div className="nx-kanban-card-meta">
                      <span className="nx-kanban-card-title">{co.name}</span>
                      <span className="nx-kanban-card-sub">{[co.industry, co.location].filter(Boolean).join(' · ') || '—'}</span>
                    </div>
                  </div>
                  <div className="nx-kanban-card-row">
                    <span className="nx-muted nx-mini">{co.employees ? co.employees.toLocaleString() + ' emp' : ''}</span>
                    <Score value={co.score} />
                  </div>
                  <div className="nx-kanban-card-row">
                    <span className="nx-tag">{co.revenue}</span>
                    <Avatar name={co.owner} tone="var(--nx-accent)" size={18} />
                    <span className="nx-muted nx-mini">{co.lastActivity}</span>
                  </div>
                </button>
              ))}
              {items.length === 0 && <div className="nx-kanban-empty"><NexusIcon name="Building" size={14} /><span className="nx-muted nx-mini">Drop a company here</span></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
window.CompaniesKanban = CompaniesKanban;

const CompaniesCalendar = ({ rows, onOpen }) => (
  <RecordCalendar
    rows={rows}
    onOpen={onOpen}
    getSlot={(r, d, month, year) => { if (!r.createdAt) return false; const dt = new Date(r.createdAt); return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === d; }}
    getDot={(r) => r.lifecycle === 'Enterprise' ? 'var(--nx-purple, #6E3FBA)' : r.lifecycle === 'Account' ? 'var(--nx-accent)' : 'var(--nx-text-faint)'}
    getLabel={(r) => r.name}
    getTooltip={(r) => r.name + ' · ' + (r.industry || '') + ' · Last activity: ' + (r.lastActivity || '—')}
  />
);
window.CompaniesCalendar = CompaniesCalendar;

/* =========================================================
   DEALS — Calendar (placed by closeDate token)
   ========================================================= */
const DealsCalendar = ({ rows, onOpen, companies }) => {
  const store = useStore();
  const stages = store.pipeline || [];
  const dayFromClose = (s, month, year) => {
    // closeDate is stored as ISO "YYYY-MM-DD" (split off by normDeal); parse without
    // timezone conversion so date-only strings land on the correct calendar cell.
    if (!s) return null;
    const parts = String(s).split('-');
    if (parts.length !== 3) return null;
    const [yyyy, mm, dd] = parts.map(Number);
    if (isNaN(yyyy) || isNaN(mm) || isNaN(dd)) return null;
    return yyyy === year && mm - 1 === month ? dd : null;
  };
  return (
    <RecordCalendar
      rows={rows}
      onOpen={onOpen}
      getSlot={(r, d, month, year) => {
        const fromDate = dayFromClose(r.closeDate, month, year);
        return fromDate !== null && fromDate === d;
      }}
      getDot={(r) => { const s = stages.find(st => st.id === r.stageId); return s ? s.tone : 'var(--nx-text-faint)'; }}
      getLabel={(r) => r.title}
      getTooltip={(r) => {
        const co = companies.find(c => c.id === r.companyId);
        const s  = stages.find(st => st.id === r.stageId);
        return r.title + (co ? ' · ' + co.name : '') + ' · $' + r.value.toLocaleString() + (s ? ' · ' + s.name : '');
      }}
    />
  );
};
window.DealsCalendar = DealsCalendar;
